// ASSISTANT MAIN MODE — the generated board (DESIGN-WL-ASSISTANT-MAIN-VIEW-1,
// tickets/reference/white-label-redesign/ASSISTANT_BOARD_MODE_SPEC.md §2–§5).
// Nika, 2026-09-26: "ai assistant can directly render in main dashboard. it can
// make generative dashboards using all apps installed in workspace on fly."
//
// The model never draws UI. It answers with a BOARD SPEC (§2) — widget ids from
// the installed apps' own manifests with per-instance `params`, plus the six
// existing AI-widget block types for questions no widget answers. This module
// turns that spec into what components/AssistantBoardView.js paints:
//
//   validateBoardSpec   spec × vocabulary → items; an unknown widget, a block
//                       type outside the vocabulary or a duplicate is DROPPED
//                       and COUNTED ("2 OF 3 WIDGETS"), a bad size falls back to
//                       the widget's own default, a param outside the widget's
//                       schema is dropped (the body keeps its default view)
//   boardVocabularyFor  the installed apps' widgets as the view can mount them
//   boardItemsFor       spec → the view's card rows (widget body refs, block
//                       bodies + their static data, hues, footers)
//   applyBoardChunk     the streaming reducer (title + summary first, then the
//                       items one by one — §3)
//   assistantBoardGenerate / Retry / Refine / Dismiss / Show — the flows
//   pinBoardItem / pinAllBoardItems — the existing AI-widget create path
//   saveBoard / listBoards / getBoard — `sdk.execute('ai.boards', …)`
//
// STATE (declared by docs/patches/assistant-board-state-keys.patch):
//   _aiBoard         the board on screen (a view model — see
//                    `assistantBoardGenerate`), workspace-scoped
//   _aiBoardHistory  recent boards for the thread chips, max 5, workspace-scoped
//   _aiBoards        saved boards (`ai.boards.list`), workspace-scoped
//   _aiBoardPreview  UI mode: show the view without the dock gate — on the home
//                    (a board belongs to the route it was made on, §12.2)
//
// THE SERVER HALF DOES NOT EXIST YET (CORE-AI-BOARDS-GENERATE-VOCABULARY-1).
// Every `ai.boards` call goes through `sdk.execute` by name; the SDK throws
// "Unknown entity" today and that lands in the FAILURE state (one sentence +
// Try again), never a raw error and never a raw fetch.
//
// FRANK: every export is SELF-CONTAINED — no import, no module-scope constant,
// no bare call to a sibling export. frank revives each export on its own, so
// any of those is undefined at runtime (memory: frank drops module-local
// helpers; measured on this file: a static `@symbo.ls/scratch` import came back
// as the unresolvable free variable `import_scratch2`). Constants live inside
// the function that reads them; a sibling export is reached through
// `this.call('name', …)` — these are invoked as `el.call('name', …)`, so `this`
// is an element.

// ── The fixture (spec §2's example) ──────────────────────────────────────────
// Used ONLY by tests and by the `_aiBoardPreview` flag until the server's
// `ai.boards.generate` exists. Never a product answer: a preview board cannot
// pin or save (see pinBoardItem / saveBoard). A FUNCTION that returns a fresh
// copy, not a module constant: frank ships every export on its own, and a
// constant read across files belongs in globalScope.js (FA203) — a caller that
// mutated a shared fixture would also change it for every other reader.
export const assistantBoardFixture = function assistantBoardFixture() {
  return {
    v: 1,
    id: 'brd_preview',
    prompt: 'How are Q3 viewings going?',
    title: 'Q3 viewings',
    summary: '42 viewings in Q3, up 12% on Q2. 3 deals sit in proposal for more than 14 days.',
    items: [
      {
        kind: 'widget',
        widget: 'scheduling.bookings-trend',
        size: 'xl',
        params: { kind: 'viewing', range: '2026-07-01..2026-09-26', group: 'week' }
      },
      {
        kind: 'widget',
        widget: 'crm.deals-stuck',
        size: 'm',
        params: { stage: 'proposal', olderThanDays: 14 }
      },
      {
        kind: 'block',
        block: 'bars',
        size: 'm',
        title: 'Revenue by property · Q3',
        data: [
          ['Argo Business Center', 61],
          ['Vake office', 24]
        ],
        source: { app: 'bi', dataset: 'revenue', range: 'q3' }
      }
    ],
    refine: [
      { label: 'Only Argo units', prompt: 'How are Q3 viewings going for the Argo units only?' },
      { label: 'Compare with Q2', prompt: 'Compare Q3 viewings with Q2.' }
    ],
    sources: [
      { app: 'scheduling', dataset: 'bookings' },
      { app: 'crm', dataset: 'deals' },
      { app: 'bi', dataset: 'revenue' }
    ],
    generatedAt: null,
    model: 'fixture'
  }
}

// ── Hashing ──────────────────────────────────────────────────────────────────
// A stable hash of any JSON value: object keys sorted at every depth, so
// `{ a: 1, b: 2 }` and `{ b: 2, a: 1 }` hash alike (a pin's duplicate check and
// a body's mount signature both depend on it). FNV-1a, 32 bit, base 36.
export const boardSpecHash = function boardSpecHash(value) {
  const stable = (v) => {
    if (v === null || typeof v !== 'object') return JSON.stringify(v === undefined ? null : v)
    if (Array.isArray(v)) return '[' + v.map(stable).join(',') + ']'
    return (
      '{' +
      Object.keys(v)
        .filter((k) => v[k] !== undefined)
        .sort()
        .map((k) => JSON.stringify(k) + ':' + stable(v[k]))
        .join(',') +
      '}'
    )
  }
  const text = stable(value)
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return (h >>> 0).toString(36)
}

// ── The vocabulary ───────────────────────────────────────────────────────────
// What the model may name, as THIS shell can mount it: every installed app's
// manifest widgets (the takeover module on `root.workspaceProject`, the
// additive apps on `root.workspaceApps` — the same two sources and the same
// first-wins id rule as the home board's `_allModuleWidgetsFor`), each keyed by
// its widget id, reachable as `<app>.<widget>` (the spec's form) or the bare id.
// `<app>` is any alias of the owning app: its project id (`system/crm-app`),
// its key (`crm-app`) or the short name (`crm`).
//
// A widget with a persona gate (`visibleFn`) enters the vocabulary only when
// its module says yes. The home board fails OPEN on a gate it cannot run yet
// (boot race); a generated board is asked for after boot, so it fails CLOSED —
// an answer never shows a card the module hides from this member.
//
// The shell's own house widgets are not here: their bodies are inline in
// pages/main.js, not mountable components, so a model that names one is
// counted as not installed until ws-home's frame exposes them.
export const boardVocabularyFor = function boardVocabularyFor(root) {
  const SIZES = ['s', 'm', 'l', 'xl']
  const el = this
  const call = (name, ...args) =>
    el && typeof el.call === 'function' ? el.call(name, ...args) : undefined
  const r = root || {}
  const widgets = {}
  const apps = {}
  const plain = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : null)
  // A widget's own verbs, as its home card has them (functions/
  // homeWidgetFrame.js — the manifest's top-level spelling, then the legacy
  // `spec`'s): where the card opens, the hook every open runs first, and its
  // ONE square button's action (a "+ New deal" form, an "Open follow-ups"
  // view). A Fly section runs them as the home does.
  const path = (v) => (typeof v === 'string' && v.startsWith('/') && !v.startsWith('//') ? v : null)
  const fnName = (v) => (typeof v === 'string' && v ? v : null)
  // `ownerId` — the widget's app: its gate resolves in THAT app's own scope
  // (two apps may name the same gate function; the root keeps only one).
  // Fails CLOSED here (no gate yet, or a throw: hidden). The shared
  // evaluation (functions/moduleWidgetGate.js) gives the gate the shell
  // bridges and subscribes this run to the member's access (G-H).
  const gateAllows = (w, ownerId) =>
    !w.visibleFn || call('moduleWidgetGateAllows', r, w, ownerId, false) === true
  // An app's own words (its first navbar tab). `appKey` beside them is the
  // app's record key — what its name is read by (installedAppName, at render:
  // the block footer and the Fly section picker), so an app reads its App Store
  // name (Nika 2026-10-23: "app names = what a person sees in the workspace App
  // Store") where these words are the manifest's ("Analytics" for Insights).
  const labelOf = (app) => {
    const nav = app && Array.isArray(app.navbar) ? app.navbar : []
    const entry = nav.find((n) => n && typeof n.label === 'string' && n.label)
    return (entry && entry.label) || ''
  }
  const deniedWidgets = {}
  const add = (app, list) => {
    const appId = app && app.id ? String(app.id) : ''
    if (!appId || !Array.isArray(list)) return
    const key = appId.split('/').pop()
    const short = key.replace(/-app$/, '')
    const aliases = [...new Set([appId, key, short].filter(Boolean))]
    const appEntry = { id: appId, short, aliases, label: labelOf(app), appKey: key, hue: null }
    for (const w of list) {
      if (!w || typeof w !== 'object' || !w.id || !w.body) continue
      const spec = plain(w.spec) || {}
      const hue = w.hue || spec.hue || spec.iconBg || null
      if (!appEntry.hue && hue) appEntry.hue = hue
      if (widgets[w.id] || !gateAllows(w, appId)) continue
      // A widget may declare the views' `access` (functions/assistantAppViews.js,
      // U5): a member it does not allow gets the quiet line, not the body.
      if (w.access && !call('assistantAppViewAllowed', w.access, r)) {
        if (!deniedWidgets[w.id]) deniedWidgets[w.id] = { ref: short + '.' + w.id, aliases }
        continue
      }
      const own = SIZES.includes(w.size) ? w.size : SIZES.includes(spec.size) ? spec.size : null
      const fromCell = call('homeWidgetSizeForCell', spec.w, spec.h)
      const minSize = SIZES.includes(w.minSize)
        ? w.minSize
        : SIZES.includes(spec.minSize)
          ? spec.minSize
          : 's'
      widgets[w.id] = {
        ref: short + '.' + w.id,
        widgetId: w.id,
        appId,
        aliases,
        title: w.title || spec.title || w.id,
        icon: w.icon || spec.icon || 'star',
        body: w.body,
        spec,
        hue,
        defaultSize: own || (SIZES.includes(fromCell) ? fromCell : 'm'),
        minSize,
        params: plain(w.params),
        describe: typeof w.describe === 'string' ? w.describe : '',
        openRoute: path(w.openRoute) || path(spec.openRoute),
        openCall: fnName(w.openCall) || fnName(spec.openCall),
        action: plain(w.action) || plain(spec.action)
      }
    }
    for (const alias of aliases) if (!apps[alias]) apps[alias] = appEntry
  }
  const wp = r.workspaceProject
  if (wp) add(wp, wp.widgets)
  for (const app of Array.isArray(r.workspaceApps) ? r.workspaceApps : []) {
    add(app, app && app.widgets)
  }
  // APP VIEWS (functions/assistantAppViews.js): an app's read-only views and
  // the built-in packages' — kept beside the widgets (kind 'view'), so the
  // widget path validates, mounts and opens them. A view this member may not
  // see goes to `denied` (the card's quiet line), never to `widgets`.
  const denied = { ...deniedWidgets }
  const sources = call('assistantAppViewSources', r)
  for (const src of Array.isArray(sources) ? sources : []) {
    const app = src && src.app
    const appId = app && app.id ? String(app.id) : ''
    if (!appId || !Array.isArray(src.views)) continue
    const key = appId.split('/').pop()
    const short = key.replace(/-app$/, '')
    const aliases = [...new Set([appId, key, short].filter(Boolean))]
    for (const v of src.views) {
      if (!v || typeof v !== 'object' || !v.id || typeof v.component !== 'string') continue
      if (!/^[a-z0-9-]{1,60}$/.test(String(v.id)) || widgets[v.id]) continue
      if (!call('assistantAppViewAllowed', v.access, r)) {
        if (!denied[v.id]) denied[v.id] = { ref: short + '.' + v.id, aliases }
        continue
      }
      widgets[v.id] = {
        kind: 'view',
        ref: short + '.' + v.id,
        widgetId: v.id,
        appId,
        aliases,
        title: v.title || v.id,
        icon: v.icon || 'star',
        body: v.component,
        spec: {},
        hue: v.hue || null,
        defaultSize: SIZES.includes(v.size) ? v.size : 'l',
        minSize: SIZES.includes(v.minSize) ? v.minSize : 's',
        params: plain(v.params),
        describe: typeof v.describe === 'string' ? v.describe : '',
        fold: src.fold || null,
        link: plain(v.link)
      }
    }
    if (!apps[short]) {
      const appEntry = { id: appId, short, aliases, label: labelOf(app), appKey: key, hue: null }
      for (const alias of aliases) if (!apps[alias]) apps[alias] = appEntry
    }
  }
  return {
    widgets,
    apps,
    denied,
    blocks: ['numeral', 'text-block', 'list', 'chip-row', 'progress-bars', 'bar-chart']
  }
}

// ── Validation (the ONLY path a spec takes to the screen) ───────────────────
// Returns `{ spec, items, total, dropped, droppedRefs, paramsDropped }`:
//   total    items the model named (capped at 12)
//   dropped  of those, the ones not shown — not installed, unknown block type,
//            a required param with no value, or a duplicate
// Nothing here throws; malformed input is an empty board.
export const validateBoardSpec = function validateBoardSpec(spec, vocabulary) {
  const SIZES = ['s', 'm', 'l', 'xl']
  const RANK = { s: 0, m: 1, l: 2, xl: 3 }
  // The board spec's block names → the AI-widget block vocabulary
  // (server widgetDefSchema.js WIDGET_BLOCK_TYPES, components/AiWidgetBlock.js).
  const BLOCKS = {
    number: 'numeral',
    numeral: 'numeral',
    text: 'text-block',
    'text-block': 'text-block',
    list: 'list',
    chips: 'chip-row',
    'chip-row': 'chip-row',
    bars: 'progress-bars',
    'progress-bars': 'progress-bars',
    chart: 'bar-chart',
    'bar-chart': 'bar-chart'
  }
  const FORMATS = ['number', 'currency', 'percent', 'text']
  const MAX_ITEMS = 12
  const MAX_ROWS = 20
  const s = spec && typeof spec === 'object' ? spec : {}
  const vocab = vocabulary && vocabulary.widgets ? vocabulary.widgets : {}
  const text = (v, max) => (typeof v === 'string' ? v.trim().slice(0, max) : '')
  const hashOf = (v) => {
    const stable = (x) => {
      if (x === null || typeof x !== 'object') return JSON.stringify(x === undefined ? null : x)
      if (Array.isArray(x)) return '[' + x.map(stable).join(',') + ']'
      return (
        '{' +
        Object.keys(x)
          .filter((k) => x[k] !== undefined)
          .sort()
          .map((k) => JSON.stringify(k) + ':' + stable(x[k]))
          .join(',') +
        '}'
      )
    }
    const str = stable(v)
    let h = 0x811c9dc5
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i)
      h = Math.imul(h, 0x01000193)
    }
    return (h >>> 0).toString(36)
  }
  const resolve = (ref) => {
    const raw = text(ref, 120)
    if (!raw) return null
    const dot = raw.indexOf('.')
    const app = dot > 0 ? raw.slice(0, dot) : ''
    const id = dot > 0 ? raw.slice(dot + 1) : raw
    const entry = Object.prototype.hasOwnProperty.call(vocab, id) ? vocab[id] : null
    if (!entry) return null
    if (app && !(entry.aliases || []).includes(app)) return null
    return entry
  }
  const isDate = (v) => {
    if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false
    const d = new Date(v + 'T00:00:00Z')
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v
  }
  const paramOk = (value, rule) => {
    const type = rule && rule.type
    const values = rule && Array.isArray(rule.values) ? rule.values : null
    // A WORKSPACE enum: the manifest names where its values live (`valuesFrom`,
    // e.g. the CRM pipeline stage over the workspace's crm_settings row). The
    // server resolves that list for THIS caller, offers only it to the model
    // and validates the item against it (server ai-boards AiBoardVocabulary);
    // the shell cannot read the row, so a clean key the static list does not
    // name passes here and the widget body maps it (an unknown key keeps the
    // body's default view). A plain enum stays strict.
    if (
      type === 'enum' &&
      rule.valuesFrom &&
      typeof rule.valuesFrom === 'object' &&
      typeof value === 'string' &&
      value !== '' &&
      value.length <= 60 &&
      value.trim() === value
    ) {
      return true
    }
    // A LIVE enum: a built-in view's list the SHELL read from this workspace
    // (`valuesLive: true` — e.g. a site view's websites, canvas functions/
    // canvasAnswer.js canvasAnswerProjectParam). The model is offered the
    // list the catalog carried; a saved page keeps a value this list does not
    // name (yet, or any more — the list lands after a cold boot), and the view
    // maps it itself (an unknown one reads its own sentence). The values-
    // from rule's shape: a clean string key.
    if (
      type === 'enum' &&
      rule.valuesLive === true &&
      typeof value === 'string' &&
      value !== '' &&
      value.length <= 120 &&
      value.trim() === value
    ) {
      return true
    }
    // A RANGE takes a named preset its rule lists (`values`, e.g. the calendar
    // agenda's today | next7days | thisWeek | nextWeek — the view turns it into
    // days in the member's clock) OR explicit days. Checked before the plain
    // `values` gate: that gate dropped explicit days whenever presets were
    // listed, and the span check dropped every preset — measured live on dev
    // (Symbols Intranet, "What meetings do I have this week?": the model's
    // `thisWeek` was dropped and the card drew the next 7 days).
    if (type === 'range') {
      if (typeof value !== 'string') return false
      if (!value.includes('..')) return !!values && values.includes(value)
      const [a, b] = value.split('..')
      return isDate(a) && isDate(b) && a <= b
    }
    if (values && !values.includes(value)) return false
    if (type === 'enum') return !!values
    if (type === 'string') return typeof value === 'string' && value.length <= 200
    if (type === 'number' || type === 'integer') {
      if (typeof value !== 'number' || !Number.isFinite(value)) return false
      if (type === 'integer' && !Number.isInteger(value)) return false
      if (typeof rule.min === 'number' && value < rule.min) return false
      if (typeof rule.max === 'number' && value > rule.max) return false
      return true
    }
    if (type === 'boolean') return typeof value === 'boolean'
    if (type === 'date') return isDate(value)
    return false
  }
  let paramsDropped = 0
  const checkParams = (given, schema) => {
    const out = {}
    const bag = given && typeof given === 'object' && !Array.isArray(given) ? given : {}
    const rules = schema && typeof schema === 'object' ? schema : {}
    for (const name of Object.keys(bag)) {
      const rule = Object.prototype.hasOwnProperty.call(rules, name) ? rules[name] : null
      if (rule && paramOk(bag[name], rule)) out[name] = bag[name]
      else paramsDropped++
    }
    for (const name of Object.keys(rules)) {
      const rule = rules[name] || {}
      if (name in out) continue
      if (rule.default !== undefined && paramOk(rule.default, rule)) out[name] = rule.default
      else if (rule.required) return null
    }
    return out
  }
  // CONTENT-FIT (HOME_AMBIENT_SKIN_SPEC §3 law 2 — content sets the size;
  // DESIGN-WL-HOME-WIDTH-RESIZE-1): a widget takes its own DESIGNED size — the
  // manifest's size, else its cell through homeWidgetSizeForCell — and the
  // model may ask for a SMALLER size, never a larger one: a larger card only
  // adds empty space around the same body. Never below its minimum.
  const sizeFor = (wanted, entry) => {
    const floor = SIZES.includes(entry.minSize) ? entry.minSize : 's'
    const designed = SIZES.includes(entry.defaultSize) ? entry.defaultSize : 'm'
    const base = SIZES.includes(wanted) && RANK[wanted] < RANK[designed] ? wanted : designed
    return RANK[base] < RANK[floor] ? floor : base
  }
  // A block takes the smallest size its DATA needs — the model's size is not
  // read: one numeral or one sentence is S; a list or bars of up to six rows
  // is one row of two cells (M), more rows take the second row (L); a chart
  // of more than eight bars takes the full row (XL); a few chips fit S. An
  // empty block says one sentence — S. Words: up to TEXT_S characters fit an
  // S card (a 276–300 px square cell holds ~8 lines of ~35 characters, fewer
  // in Georgian); a longer block (up to the server's 280) takes two cells
  // (M), so it wraps in four to six lines and is never cut by its card.
  const TEXT_S = 140
  const blockSize = (type, data) => {
    const n = data && data.count ? Number(data.count) : 0
    if (n && type === 'text-block') return String(data.text || '').length > TEXT_S ? 'm' : 's'
    if (!n || type === 'numeral' || type === 'text-block') return 's'
    if (type === 'chip-row') return n <= 6 ? 's' : 'm'
    if (type === 'bar-chart') return n <= 8 ? 'm' : 'xl'
    return n <= 6 ? 'm' : 'l'
  }
  // The rows of a list, chip-row or bars block: an array, or `{ rows }` (the
  // board's own shape). A chip row also takes the server's canonical data,
  // `{ chips: [...] }` (server ai-boards aiBoardSchema `chip-row`; a chip is a
  // string, `{ label }` or `{ text }`), ahead of `rows`, as the server's own
  // reader does. It read only an array or `{ rows }`, so every server chip row
  // on a live board had no rows and said "Nothing for …" instead of its chips
  // (dev 2026-10-30, the canvas composer board). This is the board's ONE chip
  // reader: the home's pinned blocks (functions/homePinnedItems.js) hand their
  // stored data over as it is.
  const rowsOf = (data, kind) => {
    const bag = data && typeof data === 'object' && !Array.isArray(data) ? data : {}
    const own = kind === 'chips' && Array.isArray(bag.chips) ? bag.chips : bag.rows
    const list = Array.isArray(data) ? data : Array.isArray(own) ? own : []
    const rows = []
    for (const row of list.slice(0, MAX_ROWS)) {
      if (kind === 'chips') {
        const label = Array.isArray(row)
          ? row[0]
          : row && typeof row === 'object'
            ? (row.label ?? row.text)
            : row
        if (label != null && String(label).trim()) rows.push({ label: String(label).slice(0, 120) })
        continue
      }
      if (kind === 'list') {
        const title = Array.isArray(row)
          ? row[0]
          : row && typeof row === 'object'
            ? (row.title ?? row.label ?? row.name)
            : row
        const meta = Array.isArray(row)
          ? row[1]
          : row && typeof row === 'object'
            ? (row.meta ?? row.value)
            : ''
        if (title != null && String(title).trim()) {
          rows.push({
            title: String(title).slice(0, 120),
            meta: meta == null ? '' : String(meta).slice(0, 60)
          })
        }
        continue
      }
      const label = Array.isArray(row) ? row[0] : row && typeof row === 'object' ? row.label : null
      const value = Array.isArray(row) ? row[1] : row && typeof row === 'object' ? row.value : null
      const num = typeof value === 'number' ? value : Number(value)
      if (label != null && String(label).trim() && Number.isFinite(num)) {
        rows.push({ label: String(label).slice(0, 120), value: num })
      }
    }
    return rows
  }
  const blockData = (type, data) => {
    if (type === 'numeral') {
      const v = Array.isArray(data)
        ? Array.isArray(data[0])
          ? data[0][1]
          : data[0]
        : data && typeof data === 'object'
          ? data.value
          : data
      const ok =
        typeof v === 'number' ? Number.isFinite(v) : typeof v === 'string' && v.trim() !== ''
      // The model's own words beside the number, in the member's language
      // (server ai-boards aiBoardSchema BOARD_BLOCK_DATA `numeral`: `unit` up
      // to 24 characters, `caption` up to 120): the unit says what the number
      // counts ("viewings"), the caption what it covers ("Q3, all offices").
      // Both were dropped here, so the member saw a bare number (QG-LABELS,
      // 2026-10-31). Kept only beside a value and only when they say
      // something, so a numeral without them keeps today's `{ value, count }`
      // (and its key).
      const bag = ok && data && typeof data === 'object' && !Array.isArray(data) ? data : {}
      const unit = text(bag.unit, 24)
      const caption = text(bag.caption, 120)
      return {
        value: ok ? v : null,
        count: ok ? 1 : 0,
        ...(unit ? { unit } : {}),
        ...(caption ? { caption } : {})
      }
    }
    if (type === 'text-block') {
      const t = typeof data === 'string' ? data : data && typeof data === 'object' ? data.text : ''
      // DISPLAY text, on the server's own budget (BOARD_SUMMARY_MAX = 280,
      // server ai-boards aiBoardSchema `text-block`). It was a raw
      // `slice(0, 120)`: a Fly word block lost its second half mid-word
      // ("…or web a", QA 2026-10-18). A longer string (an older server, the
      // dashboard fallback) is cut at a word boundary and marked with an
      // ellipsis inside the budget — the server's own `text()` rule — never
      // mid-word.
      const MAX = 280
      const one = typeof t === 'string' ? t.replace(/\s+/g, ' ').trim() : ''
      let clean = one
      if (one.length > MAX) {
        const cut = one.slice(0, MAX - 1)
        const space = cut.lastIndexOf(' ')
        const head = (space > (MAX - 1) * 0.5 ? cut.slice(0, space) : cut).replace(
          /[\s,;:.–—-]+$/,
          ''
        )
        clean = (head || cut) + '…'
      }
      // The SEMANTIC tone of the words (design-d2: `data.tone` = neutral |
      // good | warning | critical; the typographic style is `textStyle` on a
      // def). Anything else is dropped.
      const tone =
        data &&
        typeof data === 'object' &&
        ['neutral', 'good', 'warning', 'critical'].includes(data.tone)
          ? data.tone
          : null
      return { text: clean, ...(tone ? { tone } : {}), count: clean ? 1 : 0 }
    }
    const kind = type === 'list' ? 'list' : type === 'chip-row' ? 'chips' : 'bars'
    const rows = rowsOf(data, kind)
    return { rows, count: rows.length }
  }
  const blockDef = (type, format, data) => {
    const fmt = FORMATS.includes(format) ? { format } : {}
    if (type === 'numeral') {
      // The numeral renderer's fields (components/AiWidgetBlock.js
      // AiWidgetNumeral): `unit` is the word drawn beside the number the
      // house hero's way (a grey word that wraps, never cut; `suffix` stays
      // a sign inside the number's own text, "42%"), and `caption` is the
      // line under it. Neither is set without its words: a numeral with
      // none draws as before, with no empty caption line.
      const d = data && typeof data === 'object' ? data : {}
      return {
        type,
        valuePath: 'value',
        ...fmt,
        ...(d.unit ? { unit: d.unit } : {}),
        ...(d.caption ? { caption: d.caption } : {})
      }
    }
    if (type === 'text-block') return { type, textPath: 'text', tonePath: 'tone' }
    if (type === 'list') {
      return { type, itemsPath: 'rows', titleField: 'title', metaField: 'meta', limit: MAX_ROWS }
    }
    if (type === 'chip-row')
      return { type, itemsPath: 'rows', labelField: 'label', limit: MAX_ROWS }
    return {
      type,
      itemsPath: 'rows',
      labelField: 'label',
      valueField: 'value',
      limit: MAX_ROWS,
      ...fmt
    }
  }

  const items = []
  const droppedRefs = []
  const seen = new Set()
  let total = 0
  let dropped = 0
  const list = Array.isArray(s.items) ? s.items.slice(0, MAX_ITEMS) : []
  list.forEach((raw, index) => {
    total++
    const it = raw && typeof raw === 'object' ? raw : {}
    const drop = (why) => {
      dropped++
      droppedRefs.push({ index, ref: it.widget || it.block || null, why })
    }
    if (it.kind === 'widget' || (!it.kind && it.widget)) {
      const entry = resolve(it.widget)
      if (!entry) return drop('not-installed')
      const params = checkParams(it.params, entry.params)
      if (!params) return drop('params')
      const key = 'w:' + entry.widgetId + ':' + hashOf(params)
      if (seen.has(key)) return drop('duplicate')
      seen.add(key)
      items.push({
        key,
        kind: 'widget',
        ref: entry.ref,
        widgetId: entry.widgetId,
        appId: entry.appId,
        size: sizeFor(it.size, entry),
        params,
        title: text(it.title, 60) || null
      })
      return
    }
    if (it.kind === 'block' || (!it.kind && it.block)) {
      const type = Object.prototype.hasOwnProperty.call(BLOCKS, it.block) ? BLOCKS[it.block] : null
      if (!type) return drop('unknown-block')
      const data = blockData(type, it.data)
      const src = it.source && typeof it.source === 'object' ? it.source : {}
      const source = {
        app: text(src.app, 40),
        dataset: text(src.dataset, 60),
        range: text(src.range, 40)
      }
      const key = 'b:' + type + ':' + hashOf({ title: it.title || '', data, source })
      if (seen.has(key)) return drop('duplicate')
      seen.add(key)
      items.push({
        key,
        kind: 'block',
        block: type,
        size: blockSize(type, data),
        title: text(it.title, 60),
        def: blockDef(type, it.format, data),
        data,
        empty: !data.count,
        source
      })
      return
    }
    // A RECORDS BLOCK v1 (functions/assistantRecords.js): declarative — the
    // rows come from `ai.boards.records` under the member's grants, never from
    // the model. CONTENT SETS THE SIZE, the model's size is not read: one
    // number is S; a list or table of up to six rows is one row of two cells
    // (M), more rows take the second row (L); a table wider than three columns
    // takes the full row (XL); a chart is M.
    if (it.kind === 'records' || (!it.kind && typeof it.collection === 'string')) {
      const normalized =
        this && typeof this.call === 'function' ? this.call('recordsBlockNormalize', it) : null
      if (!normalized) return drop('records')
      const key = 'r:' + hashOf(normalized)
      if (seen.has(key)) return drop('duplicate')
      seen.add(key)
      const size = this.call('recordsContentSize', normalized)
      // A DRILL card (D2, assistantBoardDrill) says which card and which
      // value it came from — short strings only, anything else dropped.
      const dr =
        it.drill && typeof it.drill === 'object' && !Array.isArray(it.drill) ? it.drill : null
      const drill = dr
        ? {
            from: text(dr.from, 80),
            field: text(dr.field, 80),
            fieldLabel: text(dr.fieldLabel, 80),
            value: text(dr.value, 120),
            valueLabel: text(dr.valueLabel, 120),
            source: text(dr.source, 120)
          }
        : null
      items.push({
        key,
        kind: 'records',
        size,
        title: text(normalized.title && normalized.title.text, 60),
        records: normalized,
        ...(drill && drill.from && drill.field ? { drill } : {}),
        source: {
          app: normalized.app,
          dataset: normalized.collection,
          range: (normalized.period && normalized.period.preset) || ''
        }
      })
      return
    }
    // A UI SECTION of a Fly page (server ai-boards flyReads.js, Fly train 2):
    // ONE read's result, drawn with the chat's own answer-UI components — the
    // payload a chat tool call carries (`ui`: render_cards / render_answer_ui
    // and its args), `empty` (the read found nothing) or `error` (it did not
    // come back) once the read is in, none of them while it is still reading.
    // The payload comes from the read, never from the model, and the chat's
    // own gates run on it again when it mounts (functions/assistantFlyUi.js).
    // The key never reads the payload: the card stays the same card when its
    // result lands. The model's size is kept (a result's size is not known
    // when its section opens).
    if (it.kind === 'ui') {
      const read = text(it.read, 16)
      const tool = text(it.tool, 60)
      if (!read || !tool) return drop('ui')
      const raw = it.ui && typeof it.ui === 'object' && !Array.isArray(it.ui) ? it.ui : null
      const ui =
        raw &&
        (raw.tool === 'render_cards' || raw.tool === 'render_answer_ui') &&
        raw.args &&
        typeof raw.args === 'object' &&
        !Array.isArray(raw.args)
          ? { tool: raw.tool, args: raw.args }
          : null
      const er = !ui && it.error && typeof it.error === 'object' ? it.error : null
      const error = er
        ? {
            code: text(er.code, 40) || 'read_failed',
            status: Number.isFinite(Number(er.status)) ? Number(er.status) : null
          }
        : null
      const own = text(it.title && typeof it.title === 'object' ? it.title.text : it.title, 60)
      const pinKey = text(it.pinKey, 40)
      const key = 'u:' + hashOf({ read, tool, title: own, pinKey })
      if (seen.has(key)) return drop('duplicate')
      seen.add(key)
      // Its words when the model gave none: the view's own heading, else the
      // read's name ("list_projects" → "Projects").
      const words = tool
        .replace(/^(?:get|list|search|read|run)_/, '')
        .replace(/[_-]+/g, ' ')
        .trim()
      const heading =
        own ||
        (ui && ui.tool === 'render_answer_ui' ? text(ui.args.title, 60) : '') ||
        (words ? words.charAt(0).toUpperCase() + words.slice(1) : '')
      items.push({
        key,
        kind: 'ui',
        read,
        tool,
        size: SIZES.includes(it.size) ? it.size : 'm',
        title: heading,
        ui,
        empty: !ui && !error && it.empty === true,
        error,
        pinKey: pinKey || null
      })
      return
    }
    drop('unknown-kind')
  })

  const refine = (Array.isArray(s.refine) ? s.refine : [])
    .map((r) => ({ label: text(r && r.label, 40), prompt: text(r && r.prompt, 500) }))
    .filter((r) => r.label && r.prompt)
    .slice(0, 6)
  const sources = (Array.isArray(s.sources) ? s.sources : [])
    .map((x) => ({ app: text(x && x.app, 40), dataset: text(x && x.dataset, 60) }))
    .filter((x) => x.app)
    .slice(0, 12)
  return {
    spec: {
      v: 1,
      id: text(s.id, 80) || null,
      prompt: text(s.prompt, 500),
      title: text(s.title, 60),
      summary: text(s.summary, 400),
      refine,
      sources,
      generatedAt: text(s.generatedAt, 40) || null,
      model: text(s.model, 80) || null
    },
    items,
    total,
    dropped,
    droppedRefs,
    paramsDropped
  }
}

// ── Spec → the view's card rows ─────────────────────────────────────────────
// A widget row carries what the home board's module host carries
// (`{ id, title, icon, body, spec, moduleId }`, pages/main.js
// `_allModuleWidgetsFor`) plus `size` and `params`, so a body mounts here
// exactly as it mounts on the home. A block row is an AI-widget host row
// (`{ id, body: { blocks }, viewerCapability }`, components/WidgetRenderer.js)
// with its static data riding beside it; `gen:` ids can never collide with a
// stored widget (server ids are `ai-…`).
export const boardItemsFor = function boardItemsFor(spec, root) {
  const vocab = this.call('boardVocabularyFor', root)
  const res = this.call('validateBoardSpec', spec, vocab)
  const boardId =
    res.spec.id || 'brd_' + this.call('boardSpecHash', res.spec.prompt + '|' + res.spec.title)
  // Where a records card opens: the page of the app that owns its collection
  // (functions/assistantRecords.js assistantRecordsHref — the record-route
  // registry, then the app's own page), so a Fly section's title and ↗ open
  // it as a widget's do. Null on a host without the records functions.
  const fns = (this && this.context && this.context.functions) || {}
  const recordsRoute = (records) => {
    if (typeof fns.assistantRecordsHref !== 'function' || !records) return null
    const href = this.call('assistantRecordsHref', records, null)
    return typeof href === 'string' && href.startsWith('/') ? href : null
  }
  const items = res.items.map((it, index) => {
    if (it.kind === 'widget') {
      const entry = vocab.widgets[it.widgetId]
      // The widget's own square-button action rides as a frozen copy: static
      // manifest data the card only reads (a leaf of the card's state).
      const action =
        entry.kind !== 'view' && entry.action ? Object.freeze({ ...entry.action }) : null
      return {
        key: it.key,
        kind: 'widget',
        index,
        id: entry.widgetId,
        ref: it.ref,
        moduleId: entry.appId,
        title: it.title || entry.title,
        icon: entry.icon,
        body: entry.body,
        spec: entry.spec,
        hue: entry.hue,
        size: it.size,
        params: it.params,
        openRoute:
          entry.kind === 'view'
            ? this.call('assistantAppViewLink', entry.link, it.params, root)
            : entry.openRoute || null,
        openCall: entry.kind === 'view' ? null : entry.openCall || null,
        action,
        ...(entry.kind === 'view' ? { view: true, fold: entry.fold || null } : {})
      }
    }
    // A Fly page's UI section (validateBoardSpec above): the read it shows and
    // what came back — the card body mounts it with the chat's own renderers
    // (functions/assistantFlyUi.js).
    if (it.kind === 'ui') {
      return {
        key: it.key,
        kind: 'ui',
        index,
        id: 'gen:' + boardId + ':' + it.key,
        read: it.read,
        tool: it.tool,
        title: it.title,
        icon: 'analytics',
        size: it.size,
        ui: it.ui,
        empty: it.empty,
        error: it.error,
        pinKey: it.pinKey,
        hue: null,
        params: {}
      }
    }
    const app = it.source.app && vocab.apps[it.source.app] ? vocab.apps[it.source.app] : null
    const apps = new Set((res.spec.sources || []).map((x) => x.app))
    // A generated row's id is its VALIDATION key (the block's type + title +
    // data + source hashed), never its index: the cold path re-derives the
    // board when the installed apps publish (assistantBoardRederive) and a
    // widget item joins at its place, so the blocks after it MOVE — an
    // index-based id then read the block data the card before it had stashed
    // under that index (`root._aiWidgetData[id]`), and the card keyed by the
    // same validation key was reused without a re-mount: the list showed its
    // empty line, the numeral an em dash (ws-home, dev e09282f9a).
    if (it.kind === 'records') {
      return {
        key: it.key,
        kind: 'records',
        index,
        id: 'gen:' + boardId + ':' + it.key,
        title: it.title || this.call('recordsBlockTitle', it.records),
        icon: 'analytics',
        size: it.size,
        records: it.records,
        openRoute: recordsRoute(it.records),
        ...(it.drill ? { drill: it.drill } : {}),
        source: it.source,
        hue: app && app.hue ? app.hue : null,
        params: {}
      }
    }
    return {
      key: it.key,
      kind: 'block',
      index,
      id: 'gen:' + boardId + ':' + it.key,
      block: it.block,
      title: it.title,
      icon: 'magicstar',
      size: it.size,
      body: { blocks: [it.def] },
      data: it.data,
      empty: it.empty,
      viewerCapability: { allowed: true, reason: 'generated' },
      source: it.source,
      footerApp: (app && app.label) || it.source.app || '',
      // the app's record key — the footer names the app by it (see labelOf)
      footerAppKey: (app && app.label && app.appKey) || '',
      footerDataset: it.source.dataset || '',
      // One app → its colour; several apps or none → the neutral plate.
      hue: app && (apps.size <= 1 || apps.has(it.source.app)) && it.source.app ? app.hue : null,
      params: it.source.range ? { range: it.source.range } : {}
    }
  })
  return { boardId, spec: res.spec, items, total: res.total, dropped: res.dropped }
}

// ↗ OPEN on a card (a Fly section's title and its square button): a view's
// own page with the filters the view RESOLVED — the route its mounted body
// declares (`openRoute`, functions/assistantAppViews.js) — else the row's own
// route (a view's static link, a widget's openRoute). `this` is the clicked
// element inside the card; the body sits in the card's CardBody.
export const assistantBoardCardOpen = function assistantBoardCardOpen(s, event) {
  const host = typeof this.lookup === 'function' ? this.lookup('CardBody') : null
  const own = this.call('assistantAppViewOwnRoute', host)
  if (!own) return this.call('homeWidgetOpen', s, event)
  const row = s && typeof s === 'object' ? s : {}
  return this.call(
    'homeWidgetOpen',
    { id: row.id, openRoute: own, openCall: row.openCall || null, spec: null },
    event
  )
}

// ── The streaming reducer ────────────────────────────────────────────────────
// The board keeps the RAW spec as it streams and re-derives its rows from it
// after every chunk (12 items at most, so the re-derive is free), which makes a
// chunk idempotent and keeps `total` / `dropped` honest mid-stream.
//
// Chunks (any shape the server settles on; unknown fields are ignored):
//   { title, summary }                  the head — the greeting line fills first
//   { count } / { sizes: ['xl','m'] }   how many cells to hold (and their sizes)
//   { item } / { items: [...] }         one or more items, in order
//   { refine } / { sources }
//   { type: 'done', spec }              the full spec (authoritative)
//   { type: 'error', message }          the failure state
export const applyBoardChunk = function applyBoardChunk(board, chunk, root) {
  const SIZES = ['s', 'm', 'l', 'xl']
  if (!board || !chunk || typeof chunk !== 'object') return board
  if (chunk.type === 'error') {
    return this.call('assistantBoardFailed', board, 'failed')
  }
  const raw = board.raw ? { ...board.raw } : { v: 1, prompt: board.prompt, items: [] }
  const done = chunk.type === 'done'
  const full = done && chunk.spec && typeof chunk.spec === 'object' ? chunk.spec : null
  if (full) {
    Object.assign(raw, full)
    raw.items = Array.isArray(full.items) ? full.items.slice() : raw.items || []
  } else {
    for (const key of ['id', 'title', 'summary', 'refine', 'sources', 'generatedAt', 'model']) {
      if (chunk[key] !== undefined) raw[key] = chunk[key]
    }
    const add = Array.isArray(chunk.items) ? chunk.items : chunk.item ? [chunk.item] : []
    if (add.length) raw.items = (raw.items || []).concat(add)
  }
  const derived = this.call('boardItemsFor', raw, root)
  const count = Number(chunk.count)
  const expected =
    Number.isFinite(count) && count >= 0 ? Math.min(12, Math.floor(count)) : board.expected
  const sizes = Array.isArray(chunk.sizes)
    ? chunk.sizes.filter((x) => SIZES.includes(x)).slice(0, 12)
    : board.sizes
  return {
    ...board,
    status: done ? 'ready' : 'streaming',
    raw,
    boardId: derived.boardId,
    spec: derived.spec,
    items: derived.items,
    total: derived.total,
    dropped: derived.dropped,
    expected: done ? derived.items.length : Math.max(expected || 0, derived.items.length),
    sizes: sizes || null,
    receivedAt: board.receivedAt || Date.now(),
    error: null
  }
}

// The failure state: the sentence names the reason; the PREVIOUS board — when
// there was one — stays on screen underneath it (spec §4 "the previous board
// stays"); with none, the view holds the sentence and Try again alone.
export const assistantBoardFailed = function assistantBoardFailed(board, reason) {
  const why = reason === 'timeout' || reason === 'unavailable' ? reason : 'failed'
  const prev = board && board.prev
  const kept = prev
    ? {
        raw: prev.raw,
        boardId: prev.boardId,
        spec: prev.spec,
        items: prev.items,
        total: prev.total,
        dropped: prev.dropped,
        receivedAt: prev.receivedAt
      }
    : { raw: null, spec: null, items: [], total: 0, dropped: 0 }
  return { ...(board || {}), ...kept, status: 'failed', error: why, expected: 0, sizes: null }
}

// ── The view model the components read ──────────────────────────────────────
// `root._aiBoard` when it belongs to the workspace on screen, else — behind the
// preview flag — the fixture board; else null. Every key is read up front so a
// reactive caller subscribes to all of them whichever branch runs.
export const assistantBoardViewModel = function assistantBoardViewModel(root) {
  const r = root || {}
  const board = r._aiBoard
  const preview = r._aiBoardPreview === true
  const ws = r.workspace
  const live = (ws && (ws.id || ws._id)) || null
  if (board && typeof board === 'object') {
    if (!board.workspaceId || !live || String(board.workspaceId) === String(live)) return board
  }
  if (!preview) return null
  const derived = this.call('boardItemsFor', this.call('assistantBoardFixture'), r)
  return {
    v: 1,
    status: 'ready',
    requestId: 'preview',
    preview: true,
    prompt: derived.spec.prompt,
    raw: null,
    boardId: derived.boardId,
    spec: derived.spec,
    items: derived.items,
    total: derived.total,
    dropped: derived.dropped,
    expected: derived.items.length,
    sizes: null,
    error: null,
    prev: null,
    pins: {},
    pinAll: null,
    save: null,
    notice: null,
    dismissed: false,
    receivedAt: null
  }
}

// Page mode on the home — the same rule the composer's send uses
// (functions/homeAiSend.js, ws-w2 f9c16c8bc): the answers set to Page
// (`assistantDock === 'main'`), the board service answering for this workspace
// (`_aiBoardsAvailable`, set by `assistantBoardProbe`) and the HOME route.
export const assistantBoardPageOnHome = function assistantBoardPageOnHome(root) {
  const r = root || {}
  const dock = r.assistantDock
  const available = r._aiBoardsAvailable === true
  const path = r.currentPath
  if (dock !== 'main' || !available) return false
  const route = this.call('stripWorkspacePrefix', typeof path === 'string' && path ? path : '/')
  return route === '/' || route === ''
}

// The BOARD view's own gate (spec §1, brief): it renders in Page mode — on the
// home in its grid area, and (AI_ASSISTANT_SPEC §12.2, main thread =
// "Everywhere") over the page of the route it was MADE on, through the main
// layer (components/AssistantMainLayer.js) — or on the home anywhere the
// `_aiBoardPreview` UI mode is on; only while a board exists and was not
// dismissed, and never while the main thread holds the main area
// (functions/assistantMainThread.js; ONE view at a time, so Esc always
// returns to the page). A board belongs to its route (`vm.path`; a board with
// none — a saved one, a chip, a link — belongs to the home). Every key is read
// up front (reactive callers).
export const assistantBoardShown = function assistantBoardShown(root) {
  const r = root || {}
  const preview = r._aiBoardPreview === true
  const explicit = r._aiBoardExplicit
  const path = r.currentPath
  const dock = r.assistantDock
  const available = r._aiBoardsAvailable === true
  const page = this.call('assistantBoardPageOnHome', r)
  const vm = this.call('assistantBoardViewModel', r)
  const fns = this.context && this.context.functions
  const thread =
    fns && typeof fns.assistantMainThreadOpen === 'function'
      ? !!this.call('assistantMainThreadOpen', r)
      : false
  if (!vm || vm.dismissed) return false
  const norm = (p) => {
    const raw = typeof p === 'string' && p ? p : '/'
    const s = this.call('stripWorkspacePrefix', raw)
    const v = typeof s === 'string' ? s : raw
    return v === '' ? '/' : v.length > 1 ? v.replace(/\/+$/, '') : v
  }
  const route = norm(path)
  const home = route === '/'
  if (norm(vm.path) !== route) return false
  // AN EXPLICIT OPEN — a `?board=<id>` link or a board tab (assistantBoardSelect)
  // — shows THAT board on the home whatever the placement and the mode (ws-home's
  // live pass 2026-09-29: with the answers minimized the link loaded the board
  // and the view stayed hidden). A link is not a new preference: the stored
  // placement is untouched; Esc / Back to home / Dismiss end it.
  if (explicit && home && (vm.boardId === explicit || vm.savedId === explicit)) return true
  if (thread) return false
  if (home) return preview || page
  return dock === 'main' && available
}

// AN ASSISTANT VIEW IS OVER THE GRID AREA — the generated board or the main
// thread (AI_ASSISTANT_SPEC v1 §8). The home board's cards step under it
// (functions/homeWidgetFrame.js homeWidgetUnderView), J / K and E pass them by
// (functions/shortcutsHome.js), the boards row's Edit hides — for either view.
export const assistantBoardOpen = function assistantBoardOpen(root) {
  const r = root || {}
  const board = !!this.call('assistantBoardShown', r)
  const fns = this.context && this.context.functions
  const thread =
    fns && typeof fns.assistantMainThreadOpen === 'function'
      ? !!this.call('assistantMainThreadOpen', r)
      : false
  // …and the LIVE DECK (Presentation, functions/assistantDeck.js), and the
  // FLY canvas (functions/assistantFly.js).
  const deck =
    fns && typeof fns.assistantDeckShown === 'function'
      ? !!this.call('assistantDeckShown', r)
      : false
  const fly =
    fns && typeof fns.assistantFlyShown === 'function' ? !!this.call('assistantFlyShown', r) : false
  return board || thread || deck || fly
}

// The cells the grid paints: the rows so far, then one skeleton per cell the
// server said is coming, at its final size (CLS 0 when the server streams
// `sizes`; the default three-cell layout is the spec §2 example's xl · m · m).
export const assistantBoardCells = function assistantBoardCells(vm, cols) {
  if (!vm) return []
  const n = Math.max(1, Math.floor(Number(cols)) || 1)
  // `rev` = the board's Refresh count (D3): a widget body's mount signature
  // carries it, so a refresh re-mounts the app's own body (it reads again).
  const rev = Number(vm.rev) || 0
  const rows = (vm.items || []).map((it) => ({ ...it, cols: n, rev }))
  if (!this.call('assistantBoardBusy', vm)) return rows
  const DEFAULT = ['xl', 'm', 'm']
  const sizes = Array.isArray(vm.sizes) ? vm.sizes : []
  const want = Math.max(Number(vm.expected) || 0, rows.length)
  const cells = rows.slice()
  for (let i = rows.length; i < want; i++) {
    cells.push({
      key: 'skeleton-' + i,
      kind: 'skeleton',
      index: i,
      size: sizes[i] || DEFAULT[i] || 'm',
      cols: n,
      rev
    })
  }
  return cells
}

// ── Layout (ws-home's metrics table is the only source of numbers) ─────────
// The grid metrics the view lays out with: the MEASURED ones from the view's
// own state once `assistantBoardSyncGrid` has read its width, else the widest
// step of the table (functions/homeGridMetrics.js — no second copy here).
export const assistantBoardMetrics = function assistantBoardMetrics(s) {
  if (s && Number(s.cols) > 0 && Number(s.cell) > 0) {
    return {
      cols: Number(s.cols),
      gutter: Number(s.gutter) || 0,
      cell: Number(s.cell),
      square: s.square !== false
    }
  }
  const widest = this.call('homeGridMetrics', Number.MAX_SAFE_INTEGER)
  return {
    cols: widest.cols,
    gutter: widest.gutter,
    cell: widest.cell,
    square: widest.square !== false
  }
}

// Read the view's width once it is attached (onMounted) and on every window
// resize; store the metrics in the view's OWN state (geometry of one element,
// never root state). A zero width (not laid out, display none) keeps the last
// reading.
export const assistantBoardSyncGrid = function assistantBoardSyncGrid(el, s) {
  const host = el || this
  const m = this.call('assistantBoardMeasure', host)
  if (!m || !(m.cell > 0) || !s || typeof s.update !== 'function') return false
  const square = m.square !== false
  if (s.cols === m.cols && s.cell === m.cell && s.gutter === m.gutter && s.square === square)
    return false
  s.update({ cols: m.cols, gutter: m.gutter, cell: m.cell, square })
  return true
}

// The grid's inline geometry: square cells at the table's cell side (one
// column: rows as tall as their card — the table's phone step), the table's
// gutter, an ordered dense flow (iPad-style, no holes).
export const assistantBoardGridStyle = function assistantBoardGridStyle(s) {
  const m = this.call('assistantBoardMetrics', s)
  const cell = m.cell + 'px'
  return {
    gridTemplateColumns: 'repeat(' + m.cols + ', minmax(0, ' + cell + '))',
    gridAutoRows: m.square === false ? 'auto' : cell,
    columnGap: m.gutter + 'px',
    rowGap: m.gutter + 'px'
  }
}

// A card's inline style: its span at the grid's column count (homeWidgetSpan —
// XL clamps to the columns there are, L keeps two rows on one column) and the
// hue variables the slot components read.
export const assistantBoardCardStyle = function assistantBoardCardStyle(s, designSystem) {
  const span = this.call('homeWidgetSpan', (s && s.size) || 's', (s && s.cols) || 1)
  return {
    gridColumn: 'span ' + span.c,
    gridRow: 'span ' + span.r,
    ...this.call('assistantBoardHueVars', s, designSystem)
  }
}

// IS THE CARD'S CONTENT KNOWN TO BE EMPTY? `true` — its data answered and the
// body says ONE sentence and nothing else (nothing matches, not available,
// not yours, failed); `false` — it shows something; `null` — not known yet (a
// skeleton, a read in flight, a body that does not say). A block knows at
// validation (`empty`); a records card by its cache entry's status (the rows
// are read under the member's grants); a widget or a view by its BODY: a body
// declares `empty` ONCE on its root (the home slot grid's contract,
// components/HomeWidgetBody.js — a view's in functions/assistantAppViews.js),
// and this reads it on the mounted body (`CardBody.content`), so a reactive
// caller follows the body's own data. `el` is the card.
export const assistantBoardCardEmpty = function assistantBoardCardEmpty(el, s) {
  if (!s || s.kind === 'skeleton') return null
  if (s.kind === 'block') return !!s.empty
  // A Fly UI section: its read found nothing or did not come back — one
  // sentence; its result drawn — something, unless the chat's own gate
  // refused it when it mounted (the body's one quiet line says `empty`).
  if (s.kind === 'ui') {
    if (s.error || s.empty === true) return true
    if (!s.ui) return null
    const mounted = el && el.CardBody && el.CardBody.content
    if (!mounted) return null
    return mounted.empty === true
  }
  if (s.kind === 'records') {
    const entry = this.call('assistantRecordsEntry', s.root, this.call('recordsBlockOf', s))
    const status = entry && entry.status
    if (!status || status === 'loading') return null
    return status !== 'ready'
  }
  const host = el && el.CardBody
  const body = host && host.content
  if (!body) return null
  const empty = body.empty
  return empty === true ? true : empty === false ? false : null
}

// The board is still answering: the request is out and the spec may still grow
// (it streams in pieces — the summary lands before the refine chips do).
export const assistantBoardBusy = function assistantBoardBusy(vm) {
  return !!vm && (vm.status === 'loading' || vm.status === 'streaming')
}

// The head's two soft slots hold their FINAL height from the first paint, so
// nothing below them moves when the answer lands (spec §6.1 wants CLS 0;
// measured dy +113 px for the summary and +36 px for the chip row). Answer A:
// two summary lines plus one chip row. A reservation is for content that is
// COMING or already here — a settled board that got neither keeps no blank
// space for one.
export const assistantBoardSummaryReserved = function assistantBoardSummaryReserved(vm) {
  return this.call('assistantBoardBusy', vm) || !!(vm && vm.spec && vm.spec.summary)
}
export const assistantBoardRefineReserved = function assistantBoardRefineReserved(vm) {
  return (
    this.call('assistantBoardBusy', vm) ||
    !!(vm && vm.spec && vm.spec.refine && vm.spec.refine.length)
  )
}

// The summary line's rows: the model's summary as facts + connectives, or —
// while the board is being built — one line saying so (a live region must hold
// SOMETHING before the answer lands, so the change is announced).
export const assistantBoardSummaryRows = function assistantBoardSummaryRows(vm) {
  if (!vm) return []
  const summary = vm.spec && vm.spec.summary
  if (summary) return this.call('boardSummarySegments', summary)
  if (!this.call('assistantBoardBusy', vm)) return []
  return [
    {
      key: 'building',
      text: this.call('polyglot', 'workspace.assistantBoard.building'),
      fact: false
    }
  ]
}

// The action row's state: which label each capsule shows (AppLabelStack index)
// and whether the actions can run at all (a ready, real board with cards).
export const assistantBoardActionsIdle = function assistantBoardActionsIdle(vm) {
  return !vm || vm.preview || vm.status !== 'ready' || !(vm.items || []).length
}
export const assistantBoardPinAllLabel = function assistantBoardPinAllLabel(vm) {
  if (vm && vm.pinAll === 'pinning') return 1
  return vm && vm.pinAll === 'pinned' ? 2 : 0
}
export const assistantBoardSaveLabel = function assistantBoardSaveLabel(vm) {
  if (vm && vm.save === 'saving') return 1
  return vm && vm.save === 'saved' ? 2 : 0
}

// The saved record a board on screen IS, or null: the id it was saved under
// (`savedId`, set by the save), or its own id when that is a saved record's
// id (a board opened from the boards row) — looked up in the saved list
// (`root._aiBoards`), so a board that is saved reads "Saved" however it was
// opened (DESIGN-WL-HOME-WIDTH-RESIZE-1 (b)).
export const assistantBoardSavedId = function assistantBoardSavedId(root, ids) {
  const saved = Array.isArray(root && root._aiBoards) ? root._aiBoards : []
  for (const id of Array.isArray(ids) ? ids : [ids]) {
    if (id && saved.some((b) => b && b.id === id)) return id
  }
  return null
}

// One card's pin state on the board on screen (undefined when never pinned).
export const assistantBoardPinState = function assistantBoardPinState(root, key) {
  const vm = this.call('assistantBoardViewModel', root)
  return vm && vm.pins ? vm.pins[key] : undefined
}

// The thread chips show in Page mode on the home (or under the preview UI
// mode) once there is a board to go back to.
export const assistantBoardChipsOpen = function assistantBoardChipsOpen(root) {
  const r = root || {}
  const preview = r._aiBoardPreview === true
  const page = this.call('assistantBoardPageOnHome', r)
  const chips = this.call('assistantBoardChips', r)
  return chips.length > 0 && (preview || page)
}

// ── Copy ─────────────────────────────────────────────────────────────────────
// The head micro-label: "GENERATED · 3 WIDGETS · JUST NOW" — short, never the
// app list (each card's footer names its own source). Uppercase is the label's
// CSS, never JS (Mkhedruli has no case).
export const assistantBoardMicro = function assistantBoardMicro(vm) {
  if (!vm) return ''
  if (vm.status === 'loading' || vm.status === 'streaming') {
    return this.call('polyglot', 'workspace.assistantBoard.microLoading')
  }
  const shown = (vm.items || []).length
  const total = Math.max(Number(vm.total) || 0, shown)
  const at = (vm.spec && vm.spec.generatedAt) || vm.receivedAt || null
  const when = at ? this.call('timeAgo', at) || '' : ''
  if (shown < total) {
    return this.call('polyglot', 'workspace.assistantBoard.microPartial')
      .replace('{shown}', String(shown))
      .replace('{total}', String(total))
      .replace('{when}', when)
  }
  if (shown === 1) {
    return this.call('polyglot', 'workspace.assistantBoard.microOne').replace('{when}', when)
  }
  return this.call('polyglot', 'workspace.assistantBoard.micro')
    .replace('{count}', String(shown))
    .replace('{when}', when)
}

// The status sentence under the head: a failure (with Try again), an empty
// board, or the last pin / save notice. '' when there is nothing to say.
export const assistantBoardStatus = function assistantBoardStatus(vm) {
  if (!vm) return ''
  if (vm.status === 'failed') {
    if (vm.error === 'timeout') return this.call('polyglot', 'workspace.assistantBoard.timeout')
    if (vm.error === 'unavailable')
      return this.call('polyglot', 'workspace.assistantBoard.unavailable')
    return this.call('polyglot', 'workspace.assistantBoard.failed')
  }
  if (vm.status === 'ready' && !(vm.items || []).length) {
    return (vm.total || 0) > 0
      ? this.call('polyglot', 'workspace.assistantBoard.noneInstalled')
      : this.call('polyglot', 'workspace.assistantBoard.empty')
  }
  if (vm.notice === 'pinFailed') return this.call('polyglot', 'workspace.assistantBoard.pinFailed')
  if (vm.notice === 'pinDuplicate')
    return this.call('polyglot', 'workspace.assistantBoard.pinDuplicate')
  if (vm.notice === 'saveFailed')
    return this.call('polyglot', 'workspace.assistantBoard.saveFailed')
  // D4 saved boards: what a refused write said.
  if (vm.notice === 'renameFailed')
    return this.call('polyglot', 'workspace.assistantBoard.renameFailed')
  if (vm.notice === 'duplicateFailed')
    return this.call('polyglot', 'workspace.assistantBoard.duplicateFailed')
  if (vm.notice === 'deleteFailed')
    return this.call('polyglot', 'workspace.assistantBoard.deleteFailed')
  return ''
}

// The summary as the greeting's two inks (spec §4): facts in `title` 500, the
// connecting words in `inactive`, at most two sentences. A server may mark facts
// with **…**; without marks, a number with its unit word is the fact
// ("42 viewings", "12%", "USD 12.4K").
export const boardSummarySegments = function boardSummarySegments(summary) {
  const text = typeof summary === 'string' ? summary.trim() : ''
  if (!text) return []
  const sentences = text
    .split(/(?<=[.!?…])\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .join(' ')
  const out = []
  const push = (t, fact) => {
    if (t) out.push({ key: 'seg' + out.length, text: t, fact })
  }
  if (sentences.includes('**')) {
    sentences.split('**').forEach((part, i) => push(part, i % 2 === 1))
    return out
  }
  const FACT = /(?<![\p{L}\p{N}])(?:[A-Z]{3}\s)?\d(?:[\d.,]*\d)?(?:[KMB%]|\s[\p{L}][\p{L}-]*)?/gu
  let last = 0
  for (const m of sentences.matchAll(FACT)) {
    push(sentences.slice(last, m.index), false)
    push(m[0], true)
    last = m.index + m[0].length
  }
  push(sentences.slice(last), false)
  return out
}

// A widget's params as a short phrase for its empty sentence
// ("Nothing for 01/07 – 26/09 · proposal"). Dates are numeric on purpose:
// Chrome ships no Georgian locale data, so a month NAME would read English on a
// Georgian board (the timeAgo.js measurement).
export const boardParamsLabel = function boardParamsLabel(params) {
  const bag = params && typeof params === 'object' ? params : {}
  const canCall = this && typeof this.call === 'function'
  const lang = (canCall && this.call('getActiveLang')) || 'en'
  const day = (iso) => {
    const d = new Date(iso + 'T00:00:00Z')
    if (Number.isNaN(d.getTime())) return iso
    try {
      return new Intl.DateTimeFormat(lang, {
        day: '2-digit',
        month: '2-digit',
        timeZone: 'UTC'
      }).format(d)
    } catch (_) {
      return iso
    }
  }
  const parts = []
  for (const key of Object.keys(bag)) {
    const v = bag[key]
    if (v == null || v === '') continue
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}\.\.\d{4}-\d{2}-\d{2}$/.test(v)) {
      const [a, b] = v.split('..')
      parts.push(day(a) + ' – ' + day(b))
    } else if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
      parts.push(day(v))
    } else {
      parts.push(String(v))
    }
  }
  return parts.join(' · ')
}

// A size as the AI-widget record's legacy 12-column cell — the inverse of
// homeWidgetSizeForCell (functions/homeGridMetrics.js), which the pin test
// round-trips, so a pinned card keeps the size it had on the board.
export const boardSizeSpec = function boardSizeSpec(size) {
  if (size === 'xl') return { w: 12, h: 3 }
  if (size === 'l') return { w: 6, h: 6 }
  if (size === 's') return { w: 3, h: 2 }
  return { w: 6, h: 3 }
}

// ── Card chrome helpers ──────────────────────────────────────────────────────
// The two CSS custom properties the widget slot components read
// (`--home-widget-hue`, `--home-widget-hue-ink`: HomeWidgetPlate, the visuals).
// The home FRAME publishes them on its card (ws-home, in flight); until the
// view mounts through that frame, the generated card publishes them itself —
// the same order: a poster family, else a hue the manifest declared (measured
// ink), else the neutral plate that follows the theme. Delete this helper when
// the view renders through ws-home's frame.
export const assistantBoardHueVars = function assistantBoardHueVars(item, designSystem) {
  // The ON-SURFACE accent halves (`--home-widget-accent-light` /
  // `--home-widget-accent-dark`) come from the frame's own registered helper,
  // reached through `this.call` because every export in this file is
  // self-contained (see the FRANK note at the top) — one derivation, so a
  // generated card and a home card can never disagree about what a hue reads
  // as on the card surface. `null` asks for the neutral answer (the title
  // ink), which is also what the records block documents for a hue-less card.
  const accent = (bg) =>
    (this && typeof this.call === 'function'
      ? this.call('homeWidgetAccentOnSurfaceVars', bg, designSystem)
      : null) || {
      '--home-widget-accent-light': 'var(--color-title)',
      '--home-widget-accent-dark': 'var(--color-title)'
    }
  const neutral = {
    '--home-widget-hue': 'var(--color-widgetPlate)',
    '--home-widget-hue-ink': 'var(--color-title)',
    ...accent(null)
  }
  const hue = item && typeof item.hue === 'string' ? item.hue : ''
  if (!hue || !designSystem) return neutral
  const canCall = this && typeof this.call === 'function'
  const entry =
    (canCall && this.call('posterFamilyEntry', hue)) ||
    (canCall && this.call('homeWidgetHue', null, hue, designSystem)) ||
    null
  // A token the design system NAMES is published as its own variable, so a
  // branded workspace's record override (`--color-posterStudio`, the channel
  // homeWidgetHue.js documents) re-tints the plate too. A tone form
  // (`green-45` — today only the `green` alias) has no variable, and a CSS
  // custom property cannot carry a token: `'--x': 'green-45'` ships the raw
  // string (measured in jsdom — only colour PROPS resolve tone grammar). So a
  // tone form keeps the neutral plate rather than a colour guessed in JS
  // (framework gap: no token resolution for custom-property values).
  const colors = designSystem.color || {}
  const named = (token) =>
    typeof token === 'string' &&
    /^[A-Za-z][A-Za-z0-9]*$/.test(token) &&
    Object.prototype.hasOwnProperty.call(colors, token)
  if (!entry || !named(entry.bg) || !named(entry.ink)) return neutral
  return {
    '--home-widget-hue': 'var(--color-' + entry.bg + ')',
    '--home-widget-hue-ink': 'var(--color-' + entry.ink + ')',
    ...accent(entry.bg)
  }
}

// The card's square button label: "Pin to home: <title>" (twenty square
// buttons never share one accessible name).
export const assistantBoardPinLabel = function assistantBoardPinLabel(item, pinned) {
  const title = item && item.title ? String(this.call('polyglot', item.title)) : ''
  const key = pinned ? 'workspace.assistantBoard.pinnedItem' : 'workspace.assistantBoard.pinItem'
  return this.call('polyglot', key).replace('{title}', title)
}

// A block's footer: "AI · from BI revenue" — so a number the model supplied
// never reads as a live widget (spec §2).
export const assistantBoardBlockFooter = function assistantBoardBlockFooter(item) {
  // A records card's rows are LIVE (read under the member's grants), so its
  // footer names the collection, never "AI · …".
  if (item && item.kind === 'records') {
    return this.call('assistantBlockSource', { type: 'records', block: item.records })
  }
  // The app by its App Store name, in the session language (installedAppName
  // with the item's `footerAppKey` — see boardVocabularyFor's labelOf): it
  // read "AI · from Analytics revenue" for Insights. An item with no key (an
  // app the vocabulary does not know, an older pin) reads its words through
  // polyglot, as before.
  const app =
    item && item.footerApp
      ? String(
          this.call('installedAppName', { appKey: item.footerAppKey, label: item.footerApp }) ||
            this.call('polyglot', item.footerApp)
        )
      : ''
  if (!app) return this.call('polyglot', 'workspace.assistantBoard.blockFooterBare')
  return this.call('polyglot', 'workspace.assistantBoard.blockFooter')
    .replace('{app}', app)
    .replace('{dataset}', (item && item.footerDataset) || '')
    .replace(/\s+$/, '')
}

// A block with no data: one sentence WITH the params (spec §4 states).
export const assistantBoardEmptyItem = function assistantBoardEmptyItem(item) {
  const params = this.call('boardParamsLabel', (item && item.params) || {})
  const title = item && item.title ? item.title : ''
  return this.call('polyglot', 'workspace.assistantBoard.emptyItem').replace(
    '{params}',
    params || title
  )
}

// ── Body mount ───────────────────────────────────────────────────────────────
// MINIMAL EQUIVALENT of pages/main.js `_mountModuleWidgetBody` (not exported
// there, so it cannot be called): a string body extends the module component,
// under the module's OWN design-system + state context from
// `root._moduleDsCtxGet(moduleId)` (WS-MODULE-STATE-SCOPE-1) and its scope bag;
// the context's PRESENCE is in the signature so a body that mounted before the
// module context landed re-mounts exactly once when it does. Added here: the
// per-instance `params` the body fetches with (spec §2), and the block branch —
// a block's static data is seeded into the AI-widget data bag
// (`root._aiWidgetData[<gen id>]`, functions/aiWidgets.js aiWidgetState) BEFORE
// WidgetRenderer mounts, so its interpreters read it on their first run.
export const assistantBoardMountBody = function assistantBoardMountBody(el, s) {
  const host = el || this
  if (!host || !s || typeof host.set !== 'function') return
  const root = s.root
  if (s.kind === 'block') {
    const sig = 'block/' + s.id + '/' + this.call('boardSpecHash', s.data || null)
    if (host.__boardBodySig === sig) return
    host.__boardBodySig = sig
    const bag = (root && root._aiWidgetData) || {}
    const have = bag[s.id]
    if (root && typeof root.update === 'function' && !(have && have.sig === sig)) {
      root.update(
        {
          _aiWidgetData: {
            ...bag,
            [s.id]: {
              sig,
              loading: false,
              settled: true,
              denied: false,
              error: null,
              data: s.data || {}
            }
          }
        },
        { preventFetch: true }
      )
    }
    host.set({ extends: 'WidgetRenderer' })
    return
  }
  if (s.kind === 'records') {
    // The records renderer reads its block from this card's row (`records`)
    // and its rows from the shared per-workspace cache.
    const sig = 'records/' + s.id + '/' + this.call('boardSpecHash', s.records || null)
    if (host.__boardBodySig === sig) return
    host.__boardBodySig = sig
    host.set({ extends: 'AssistantRecordsBlock' })
    return
  }
  // A Fly UI section: the read's result through the chat's own answer-UI
  // renderers (functions/assistantFlyUi.js) — once it is in.
  if (s.kind === 'ui') {
    this.call('assistantFlyUiMount', host, s)
    return
  }
  if (s.kind !== 'widget' || !s.body) return
  // A widget or view body is workspace-scoped content: it waits for the live
  // workspace, as module config and dashboard prefs do (functions/
  // initialWorkspaceReveal.js). A Fly page restored at boot mounted its views
  // before the SDK had a workspace, so their reads went out with no scope —
  // measured on dev 2026-10-18: /core/docs/list and /core/file-canvas with no
  // workspaceId at 8.2 s, and the Files view listed another scope's items
  // (the five newest notes missing). The shell says "not yet" explicitly
  // (state.js: workspaceReady false, or a warm-cache boot). One wait per host,
  // whatever it answers; the card keeps its skeleton meanwhile.
  if (
    !host.__boardWsWaited &&
    root &&
    (root.workspaceReady === false || root._bootFromCache === true)
  ) {
    if (host.__boardWsWait) return
    host.__boardWsWait = true
    const after = this
    const go = () => {
      host.__boardWsWait = false
      host.__boardWsWaited = true
      if (host.node && host.node.isConnected === false) return
      after.call('assistantBoardMountBody', host, host.state || s)
    }
    Promise.resolve(this.call('awaitInitialWorkspace', root)).then(go, go)
    return
  }
  // A built-in package's VIEW (functions/assistantAppViews.js): its package is
  // lazy — fold its context first when the component is not registered yet,
  // then mount. One fold per host; the card keeps its skeleton meanwhile.
  if (s.fold && typeof s.body === 'string') {
    // A pending fold, or one that already failed for this view (its quiet
    // line stays — never a fold per update).
    if (host.__boardFoldPending || host.__boardBodySig === 'failed/' + (s.id || '')) return
    const comps = host.context && host.context.components
    if (!comps || !comps[s.body]) {
      host.__boardFoldPending = true
      const again = this
      const quiet = () => {
        // The package's chunk did not load: one quiet line, never an empty
        // frame (U5) — a body that says only that is `empty`.
        host.__boardBodySig = 'failed/' + (s.id || '')
        try {
          host.set({
            tag: 'p',
            margin: '0',
            fontSize: 'Z',
            color: 'caption',
            empty: true,
            text: String(again.call('polyglot', 'workspace.assistantBlock.viewFailed'))
          })
          again.call('assistantBoardBodyMounted', host)
        } catch (_) {
          /* the host is gone */
        }
      }
      Promise.resolve(this.call('ensureAppViewContext', s.fold)).then(
        (ok) => {
          host.__boardFoldPending = false
          if (!ok) return quiet()
          host.__boardBodySig = null
          again.call('assistantBoardMountBody', host, s)
        },
        () => {
          host.__boardFoldPending = false
          quiet()
        }
      )
      return
    }
  }
  const modCtx =
    root && typeof root._moduleDsCtxGet === 'function' ? root._moduleDsCtxGet(s.moduleId) : null
  const sig =
    'widget/' +
    (s.moduleId || '') +
    '/' +
    (s.id || '') +
    '/' +
    this.call('boardSpecHash', s.params || {}) +
    '/' +
    (modCtx ? 1 : 0) +
    '/' +
    (Number(s.rev) || 0)
  if (host.__boardBodySig === sig) return
  host.__boardBodySig = sig
  const def = typeof s.body === 'string' ? { extends: s.body } : { ...s.body }
  def.params = s.params || {}
  if (modCtx) {
    def.context = modCtx
    if (modCtx.globalScope) {
      def.scope =
        typeof def.scope === 'object' && def.scope !== null
          ? Object.assign({}, modCtx.globalScope, def.scope)
          : modCtx.globalScope
    }
  }
  // FRAME CONTRACT v1 — the home host's own shape (pages/main.js
  // _mountModuleWidgetBody): the body reads `el.scope.params` (the widget's
  // schema defaults from the vocabulary + this card's values), `widgetId` and
  // `size`. A COPY of whatever scope the body had — the app's shared
  // globalScope bag is never written (every widget of the app shares it).
  // Before, the card set `params` beside the scope, and a body reading
  // `el.scope.params` rendered its home twin (crm-w1 live leg, 2026-09-27).
  const vocab = this.call('boardVocabularyFor', root)
  const entry = vocab && vocab.widgets ? vocab.widgets[s.id] : null
  const schema = entry && entry.params && typeof entry.params === 'object' ? entry.params : {}
  const defaults = {}
  for (const [name, d] of Object.entries(schema)) {
    if (d && typeof d === 'object' && d.default !== undefined) defaults[name] = d.default
  }
  def.scope = Object.assign({}, def.scope || null, {
    params: { ...defaults, ...(s.params || {}) },
    widgetId: s.id,
    size: s.size || 's',
    // A VIEW reads `embedded` to leave out its page chrome and every write
    // control — an answer shows the app's UI read-only.
    ...(s.view ? { embedded: true } : {})
  })
  try {
    host.set(def)
  } catch (err) {
    console.warn('[assistantBoard] widget body mount failed:', s.id, err && err.message)
    // A VIEW that cannot mount says so in one quiet line (U5), never an empty
    // frame; a board widget keeps its own frame's empty state.
    if (s.view) {
      try {
        host.set({
          tag: 'p',
          margin: '0',
          fontSize: 'Z',
          color: 'caption',
          empty: true,
          text: String(this.call('polyglot', 'workspace.assistantBlock.viewFailed'))
        })
      } catch (_) {
        /* the host is gone */
      }
    }
  }
  this.call('assistantBoardBodyMounted', host)
}

// A body was mounted into `host`: the card that holds it (the host's parent)
// may size itself by what the body says (assistantBoardCardEmpty reads the
// body's `empty` — a Fly section, functions/assistantFly.js
// assistantFlyCardStyle). The new body is a new element its reactive props
// never read, so they run once more: the empty-patch update re-runs the
// card's own effects and re-subscribes them (no state write; the cards
// declare no onUpdate). Never throws.
export const assistantBoardBodyMounted = function assistantBoardBodyMounted(host) {
  const card = host && host.parent
  if (!card || !host.content || typeof card.update !== 'function') return false
  try {
    card.update()
  } catch (_) {
    return false
  }
  return true
}

// ── The two DOM touches (a11y + geometry) ────────────────────────────────────
// The ONE geometry read: the grid's own width, asked of ws-home's metrics table
// (functions/homeGridMetrics.js) — columns follow the grid, never the viewport,
// and a synchronous read works in a hidden tab where rAF / ResizeObserver never
// fire.
export const assistantBoardMeasure = function assistantBoardMeasure(el) {
  const node = el && el.node
  const width =
    node && typeof node.getBoundingClientRect === 'function'
      ? node.getBoundingClientRect().width
      : 0
  return this.call('homeGridMetrics', width)
}

// Focus moves (spec §5): the title line after a board lands (`tabindex -1`),
// the composer on Esc. Found through the DOMQL tree, never a selector.
export const assistantBoardFocus = function assistantBoardFocus(target) {
  try {
    const root = typeof this.getRoot === 'function' ? this.getRoot() : null
    if (target === 'composer') {
      const composer = root && root.lookdown ? root.lookdown('CanvasPromptTextarea') : null
      const field = composer && composer.lookdown ? composer.lookdown('Textarea') : null
      if (field && field.node && typeof field.node.focus === 'function') field.node.focus()
      return !!field
    }
    const own = typeof this.lookup === 'function' ? this.lookup('AssistantBoardView') : null
    const view = own || (root && root.lookdown ? root.lookdown('AssistantBoardView') : null)
    const title = view && view.lookdown ? view.lookdown('BoardTitle') : null
    if (title && title.node && typeof title.node.focus === 'function') {
      title.node.focus({ preventScroll: true })
    }
    return !!title
  } catch (_) {
    return false
  }
}

// Esc closes the view and returns focus to the composer; P pins the selected
// card. Two rows of the shell keyboard registry (functions/shortcuts.js), in the
// board group of the ? sheet, registered when the view mounts and taken out
// when it is removed (the view's onRemove runs the returned function). The
// registry's guard does the rest: P is a character key, so it is inert in a
// field, while composing (IMEs), with a modifier held and when the person turns
// character keys off (WCAG 2.1.4); Esc is a named key and stands down while an
// overlay owns the keyboard (a sheet, a menu, a dialog closes first). Both rows
// run only while THIS view is on the page and shown; P declines (the key stays
// the browser's) unless focus is inside one of the board's cards. Registered
// after the shell's defaults are armed, so these are the newer bindings of
// their keys and are tried first.
export const assistantBoardShortcuts = function assistantBoardShortcuts(el) {
  const view = el || this
  if (!view || typeof view.call !== 'function') return () => false
  // TWO board views register these — the home's and the page layer's
  // (components/AssistantMainLayer.js, `mainHost: 'over'`): the registry keeps
  // ONE row per id and a removal takes only its own record, so the layer's
  // rows keep their own ids, unlabelled (the ? sheet lists each key once).
  const over = view.mainHost === 'over'
  view.call('listShortcuts')
  // A page SHEET above the view (the PageSlot) is the top layer: the shell's
  // own Esc row closes it first.
  const shown = (h, root) => {
    const node = view.node
    if (!node || node.isConnected === false) return false
    if (root && (root._canvasSlot || root._canvasSlotSecond)) return false
    return !!view.call('assistantBoardShown', root)
  }
  const offs = [
    view.call('registerShortcut', {
      id: over ? 'assistantBoardDismissOver' : 'assistantBoardDismiss',
      keys: 'escape',
      label: over ? null : 'workspace.assistantBoard.shortcutDismiss',
      group: 'shell.headerTabs.dashboard',
      groupOrder: 3,
      order: 6,
      when: shown,
      run: () => view.call('assistantBoardDismiss', { focusComposer: true })
    }),
    view.call('registerShortcut', {
      id: over ? 'assistantBoardPinOver' : 'assistantBoardPin',
      keys: 'p',
      label: over ? null : 'workspace.assistantBoard.shortcutPin',
      group: 'shell.headerTabs.dashboard',
      groupOrder: 3,
      order: 7,
      when: shown,
      run: (h, root, e) => {
        const t = e && e.target
        const card = t && typeof t.closest === 'function' ? t.closest('[data-board-item]') : null
        const key =
          card && typeof card.getAttribute === 'function'
            ? card.getAttribute('data-board-item')
            : null
        const vm = view.call('assistantBoardViewModel', root)
        const item = vm && key ? (vm.items || []).find((it) => it.key === key) : null
        if (!item) return false
        view.call('pinBoardItem', view, view.state, item)
        return true
      }
    })
  ]
  const off = () => offs.map((fn) => typeof fn === 'function' && fn()).some(Boolean)
  view.__boardShortcutsOff = off
  return off
}

// ── Flows ────────────────────────────────────────────────────────────────────
// Submit → the skeleton view in the SAME tick (the first root write happens
// before any network work), then the stream fills it. `this` is the element
// that asked (the composer, a refine chip, Try again, a thread chip).
//
// The board view model (`root._aiBoard`):
//   { status: 'loading'|'streaming'|'ready'|'failed', requestId, workspaceId,
//     prompt, op, refineOf, threadId, startedAt, expected, sizes, raw, boardId,
//     spec, items, total, dropped, error, prev, pins, pinAll, save, notice,
//     dismissed, preview, receivedAt }
// `requestId` makes every late write of a superseded request a no-op; a
// workspace switch wipes the key (workspace-scoped), so a late chunk finds no
// board and drops itself.
// Called two ways: `(prompt, opts)` from this view's own controls, and
// `({ prompt, attachments, attached })` from the composer's send
// (functions/homeAiSend.js, ws-w2 f9c16c8bc). `attached` (the canvas
// selection's structured refs) and `attachments` ride to the server untouched —
// the server decides what a board may read; the view reads neither.
export const assistantBoardGenerate = async function assistantBoardGenerate(prompt, opts) {
  const el = this
  const root = el && el.state && el.state.root
  const input = prompt && typeof prompt === 'object' ? prompt : { prompt }
  const text = String(input.prompt == null ? '' : input.prompt).trim()
  if (!root || typeof root.update !== 'function' || !text) return null
  const o = { ...(opts || {}) }
  if (input.attached) o.attached = input.attached
  if (input.attachments) o.attachments = input.attachments
  const ws = root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  const current = root._aiBoard
  const snap = (b) => ({
    raw: b.raw,
    boardId: b.boardId,
    spec: b.spec,
    items: b.items,
    total: b.total,
    dropped: b.dropped,
    receivedAt: b.receivedAt
  })
  const prev =
    current && current.status === 'ready' && !current.preview && (current.items || []).length
      ? snap(current)
      : (current && current.prev) || null
  const requestId = 'req_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8)
  const op = o.refineOf ? 'refine' : 'generate'
  // ONE view holds the main area: a live deck on screen steps aside
  // (functions/assistantDeck.js). The board belongs to the route it is made
  // on (AI_ASSISTANT_SPEC §12.2 — the page's main area, or the home's).
  const fns = el.context && el.context.functions
  if (fns && typeof fns.assistantDeckDismiss === 'function') el.call('assistantDeckDismiss')
  if (fns && typeof fns.assistantFlyClose === 'function') el.call('assistantFlyClose')
  const madeOn = typeof root.currentPath === 'string' && root.currentPath ? root.currentPath : '/'
  root.update(
    {
      _aiBoard: {
        v: 1,
        status: 'loading',
        requestId,
        workspaceId,
        path: madeOn,
        prompt: text,
        op,
        refineOf: o.refineOf || null,
        refineSpec: o.refineSpec || null,
        threadId: o.threadId || null,
        startedAt: Date.now(),
        expected: 3,
        sizes: null,
        raw: null,
        boardId: null,
        spec: null,
        items: [],
        total: 0,
        dropped: 0,
        error: null,
        prev,
        pins: {},
        pinAll: null,
        save: null,
        notice: null,
        dismissed: false,
        preview: false,
        receivedAt: null
      }
    },
    { preventFetch: true }
  )
  const isCurrent = () => {
    const b = root._aiBoard
    return !!b && b.requestId === requestId
  }
  const apply = (chunk) => {
    if (!isCurrent()) return
    // A failed request is terminal: after the budget fired (spec §4 — the
    // sentence + Try again + the previous board kept), a late stream frame of
    // the SAME request must not resurrect it into a 'streaming' board whose
    // `done` can never arrive (the race already rejected). Only Try again or
    // a new request (new requestId) replaces a failure.
    const cur = root._aiBoard
    if (cur && cur.status === 'failed') return
    const next = el.call('applyBoardChunk', cur, chunk, root)
    if (next && isCurrent()) root.update({ _aiBoard: next }, { preventFetch: true })
  }
  const fail = (reason) => {
    if (!isCurrent()) return
    root.update(
      { _aiBoard: el.call('assistantBoardFailed', root._aiBoard, reason) },
      { preventFetch: true }
    )
  }
  const reasonOf = (err) => {
    const msg = String((err && err.message) || err || '')
    if (/timeout|timed out/i.test(msg)) return 'timeout'
    if (
      /unknown entity|does not support op|is not available|not found on service|unknown method/i.test(
        msg
      )
    ) {
      return 'unavailable'
    }
    return 'failed'
  }
  const sdk = el.sdk || (typeof el.call === 'function' ? el.call('getSDK') : null)
  // The server contract names the budget; until it exists the view waits 45 s
  // (spec §2: the TARGET is ≤ 8 s, measured on dev before a number is claimed).
  const timeoutMs = Number(o.timeoutMs) > 0 ? Number(o.timeoutMs) : 45000
  let timer = null
  try {
    if (!sdk || typeof sdk.execute !== 'function') throw new Error('unknown method: ai.boards')
    // STREAMING: the SDK streams only when the caller passes one of ITS
    // callbacks (sdk AiBoardsService `wantsStream`: onEvent / onStart / onHead
    // / onItem / onDrop). `onChunk` alone is not one of them, so every board
    // used to arrive in ONE response at the end (live defect 2026-09-28:
    // "all items arrived at the end"). The head and each item now land the
    // moment the server validates them (server AiBoardService: board.head →
    // board.item … → board.done); `onChunk` stays for the chunk-shaped
    // transports the reducer also reads.
    // REFINE of a generated board sends the board ON SCREEN (`spec`, which the
    // server re-validates); `boardId` alone only finds a SAVED board.
    // WHAT THIS SHELL RENDERS (the server's capability gate): `capabilities:
    // { records: 1 }` only when the records renderer is registered and the SDK
    // resolves records (functions/assistantRecords.js); without it the server
    // emits no records item. sdk 3.14.796 builds the body from a closed key
    // list, so there the field never leaves the client (8076ec6+ forwards it).
    // The shell's SDK answers every method with a promise: the first read of
    // a page may answer a promise — awaited; later reads answer at once.
    let capabilities = el.call('assistantClientCapabilities', sdk)
    if (capabilities && typeof capabilities.then === 'function') capabilities = await capabilities
    const pending = sdk.execute('ai.boards', op, {
      prompt: text,
      ...(o.refineOf ? { boardId: o.refineOf } : {}),
      ...(o.refineOf && o.refineSpec ? { spec: o.refineSpec } : {}),
      ...(o.threadId ? { threadId: o.threadId } : {}),
      ...(workspaceId ? { workspaceId } : {}),
      ...(o.attached ? { attached: o.attached } : {}),
      ...(o.attachments ? { attachments: o.attachments } : {}),
      ...(capabilities ? { capabilities } : {}),
      onHead: (head) => {
        if (head && typeof head === 'object') {
          apply({ id: head.id, title: head.title, summary: head.summary })
        }
      },
      onItem: (frame) => {
        if (frame && typeof frame === 'object' && frame.item) apply({ item: frame.item })
      },
      onChunk: apply
    })
    const res = await Promise.race([
      Promise.resolve(pending),
      new Promise((resolve, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), timeoutMs)
      })
    ])
    const spec = res && typeof res === 'object' ? res.spec || res.board || res : null
    apply({ type: 'done', spec: spec && Array.isArray(spec.items) ? spec : null })
  } catch (err) {
    fail(reasonOf(err))
  } finally {
    if (timer) clearTimeout(timer)
  }
  const done = root._aiBoard
  if (!done || done.requestId !== requestId) return null
  if (done.status === 'ready') {
    const entry = {
      id: done.boardId,
      title: (done.spec && done.spec.title) || text,
      prompt: text,
      raw: done.raw,
      workspaceId,
      at: Date.now()
    }
    const list = Array.isArray(root._aiBoardHistory) ? root._aiBoardHistory : []
    const same = (h) =>
      h && (h.id === entry.id || String(h.prompt || '').toLowerCase() === text.toLowerCase())
    root.update(
      { _aiBoardHistory: [entry, ...list.filter((h) => !same(h))].slice(0, 5) },
      { preventFetch: true }
    )
    el.call('assistantBoardFocus', 'title')
  }
  return done
}

// Try again: the last request, the same way.
export const assistantBoardRetry = function assistantBoardRetry() {
  const root = this.state && this.state.root
  const b = root && root._aiBoard
  if (!b || !b.prompt) return null
  return this.call('assistantBoardGenerate', b.prompt, {
    refineOf: b.refineOf || null,
    refineSpec: b.refineSpec || null,
    threadId: b.threadId || null
  })
}

// A refine chip: the chip's prompt against the board on screen (spec §3
// `refine (boardId + prompt)`).
export const assistantBoardRefine = function assistantBoardRefine(entry) {
  const root = this.state && this.state.root
  const b = root && root._aiBoard
  const prompt = entry && typeof entry === 'object' ? entry.prompt : entry
  if (!prompt) return null
  return this.call('assistantBoardGenerate', prompt, {
    refineOf: (b && !b.preview && b.boardId) || null,
    refineSpec: (b && !b.preview && b.raw) || null,
    threadId: (b && b.threadId) || null
  })
}

// D2 DRILL-DOWN (Pulse: a bars row opens the filtered canvas). A bar of a
// records chart on the board → a LIST card of that one value, RIGHT AFTER its
// source card. The card is a real records item of the raw spec (Save keeps
// it; the server re-validates it like any item and drops the `drill` words),
// so it re-derives, streams and saves like every other card. One drill per
// source: a new value replaces the last. A preview, a board still streaming
// and a full board (12 cards) take none.
export const assistantBoardDrill = function assistantBoardDrill(sourceKey, bar) {
  const MAX_ITEMS = 12
  const root = this.state && this.state.root
  const b = root && root._aiBoard
  if (!root || typeof root.update !== 'function' || !b || typeof b !== 'object') return false
  if (b.preview || b.status !== 'ready' || !b.raw || !Array.isArray(b.raw.items)) return false
  const items = Array.isArray(b.items) ? b.items : []
  const source = items.find((it) => it && it.key === sourceKey && it.kind === 'records')
  if (!source || !source.records) return false
  const entry = this.call('assistantRecordsEntry', root, source.records)
  const made = this.call('assistantRecordsDrillOf', source.records, entry, bar)
  if (!made) return false
  const keyOf = (it) => {
    if (!it || typeof it !== 'object') return ''
    const n = this.call('recordsBlockNormalize', it)
    return n ? 'r:' + this.call('boardSpecHash', n) : ''
  }
  const kept = b.raw.items.filter((it) => !(it && it.drill && it.drill.from === sourceKey))
  if (kept.length >= MAX_ITEMS) return false
  const at = kept.findIndex((it) => keyOf(it) === sourceKey)
  if (at < 0) return false
  const next = kept.slice()
  next.splice(at + 1, 0, { ...made.block, drill: { from: sourceKey, ...made.drill } })
  const raw = { ...b.raw, items: next }
  const derived = this.call('boardItemsFor', raw, root)
  root.update(
    {
      _aiBoard: {
        ...b,
        raw,
        boardId: derived.boardId,
        spec: derived.spec,
        items: derived.items,
        total: derived.total,
        dropped: derived.dropped,
        expected: derived.items.length
      }
    },
    { preventFetch: true }
  )
  return true
}

// The drill card's own close: that card leaves the board (its source stays).
// A card that is not a drill is never removed this way.
export const assistantBoardDrillClose = function assistantBoardDrillClose(key) {
  const root = this.state && this.state.root
  const b = root && root._aiBoard
  if (!root || typeof root.update !== 'function' || !b || !b.raw || !Array.isArray(b.raw.items)) {
    return false
  }
  const card = (Array.isArray(b.items) ? b.items : []).find((it) => it && it.key === key)
  if (!card || !card.drill) return false
  const next = b.raw.items.filter((it) => {
    if (!it || !it.drill) return true
    const n = this.call('recordsBlockNormalize', it)
    return !(n && 'r:' + this.call('boardSpecHash', n) === key)
  })
  const raw = { ...b.raw, items: next }
  const derived = this.call('boardItemsFor', raw, root)
  root.update(
    {
      _aiBoard: {
        ...b,
        raw,
        boardId: derived.boardId,
        spec: derived.spec,
        items: derived.items,
        total: derived.total,
        dropped: derived.dropped,
        expected: derived.items.length
      }
    },
    { preventFetch: true }
  )
  return true
}

// D3 PERIOD (Pulse: "set window 7d / 30d"). The ONE period every dated
// records card on the board shares — '' when they differ or none is dated.
export const assistantBoardPeriod = function assistantBoardPeriod(vm) {
  const items = vm && Array.isArray(vm.items) ? vm.items : []
  const presets = items
    .filter((it) => it && it.kind === 'records' && it.records && it.records.period)
    .map((it) => it.records.period.preset)
  if (!presets.length) return ''
  return presets.every((p) => p === presets[0]) ? presets[0] : ''
}

// The period row shows on a ready, real board with at least one dated card.
export const assistantBoardPeriodShown = function assistantBoardPeriodShown(vm) {
  if (!vm || typeof vm !== 'object' || vm.preview || vm.status !== 'ready') return false
  const items = Array.isArray(vm.items) ? vm.items : []
  return items.some((it) => it && it.kind === 'records' && it.records && it.records.period)
}

// The six presets of RECORDS BLOCK v1 as segmented rows (AppSegmented's row
// contract); the words are the records period keys (a literal map).
export const assistantBoardPeriodOptions = function assistantBoardPeriodOptions() {
  return [
    { key: 'today', value: 'today', text: 'workspace.assistantRecords.periodToday' },
    { key: 'thisWeek', value: 'thisWeek', text: 'workspace.assistantRecords.periodThisWeek' },
    { key: 'thisMonth', value: 'thisMonth', text: 'workspace.assistantRecords.periodThisMonth' },
    {
      key: 'thisQuarter',
      value: 'thisQuarter',
      text: 'workspace.assistantRecords.periodThisQuarter'
    },
    {
      key: 'lastQuarter',
      value: 'lastQuarter',
      text: 'workspace.assistantRecords.periodLastQuarter'
    },
    { key: 'thisYear', value: 'thisYear', text: 'workspace.assistantRecords.periodThisYear' }
  ]
}

// A new period for the whole board: every records card WITH a period takes
// the preset (its own date field kept); a card without one, a widget and a
// block stay as they are. The dated cards become new questions (new keys, a
// new read); a drill card follows its source (`from` re-keyed). The raw spec
// changes, so Save keeps the new period.
export const assistantBoardSetPeriod = function assistantBoardSetPeriod(preset) {
  const PRESETS = ['today', 'thisWeek', 'thisMonth', 'thisQuarter', 'lastQuarter', 'thisYear']
  if (!PRESETS.includes(preset)) return false
  const root = this.state && this.state.root
  const b = root && root._aiBoard
  if (!root || typeof root.update !== 'function' || !b || typeof b !== 'object') return false
  if (b.preview || b.status !== 'ready' || !b.raw || !Array.isArray(b.raw.items)) return false
  const keyOf = (it) => {
    const n = it && typeof it === 'object' ? this.call('recordsBlockNormalize', it) : null
    return n ? 'r:' + this.call('boardSpecHash', n) : ''
  }
  const moved = {}
  const dated = b.raw.items.map((it) => {
    const n = it && typeof it === 'object' ? this.call('recordsBlockNormalize', it) : null
    if (!n || !n.period) return it
    const next = { ...it, period: { ...n.period, preset } }
    const from = keyOf(it)
    const to = keyOf(next)
    if (from && to) moved[from] = to
    return next
  })
  const items = dated.map((it) =>
    it && it.drill && moved[it.drill.from]
      ? { ...it, drill: { ...it.drill, from: moved[it.drill.from] } }
      : it
  )
  const raw = { ...b.raw, items }
  const derived = this.call('boardItemsFor', raw, root)
  root.update(
    {
      _aiBoard: {
        ...b,
        raw,
        boardId: derived.boardId,
        spec: derived.spec,
        items: derived.items,
        total: derived.total,
        dropped: derived.dropped,
        expected: derived.items.length
      }
    },
    { preventFetch: true }
  )
  return true
}

// REFRESH (Pulse's Refresh button): every records card is asked again (a
// ready answer too — never one still in flight) and the board's `rev` steps,
// so each installed app's widget body re-mounts and reads again. Returns how
// many records cards were asked.
export const assistantBoardRefresh = function assistantBoardRefresh() {
  const root = this.state && this.state.root
  const b = root && root._aiBoard
  if (!root || typeof root.update !== 'function' || !b || typeof b !== 'object') return 0
  if (b.preview || b.status !== 'ready') return 0
  let asked = 0
  for (const it of Array.isArray(b.items) ? b.items : []) {
    if (!it || it.kind !== 'records' || !it.records) continue
    const have = this.call('assistantRecordsEntry', root, it.records)
    if (have && have.status === 'loading') continue
    this.call('assistantRecordsEnsure', this, { root, records: it.records }, 'refresh')
    asked++
  }
  const widgets = (Array.isArray(b.items) ? b.items : []).some((it) => it && it.kind === 'widget')
  if (asked || widgets) {
    root.update(
      { _aiBoard: { ...root._aiBoard, rev: (Number(b.rev) || 0) + 1 } },
      { preventFetch: true }
    )
  }
  return asked
}

// Refresh rests while any records card of the board is still reading.
export const assistantBoardRefreshing = function assistantBoardRefreshing(root) {
  const r = root || {}
  const b = r._aiBoard
  const bag = r._aiRecords
  if (!b || !bag) return false
  return (Array.isArray(b.items) ? b.items : []).some((it) => {
    if (!it || it.kind !== 'records' || !it.records) return false
    const e = this.call('assistantRecordsEntry', r, it.records)
    return !!e && e.status === 'loading'
  })
}

// ── D4 SAVED BOARDS: rename · duplicate · delete (Pulse's boards store v2) ──
// What the viewer may do with the board on screen. A saved board answers
// from its record (the server's viewerCanEdit / viewerCanDelete); an unsaved
// one can only be renamed (Save keeps the name); a preview or a busy board
// takes nothing.
export const assistantBoardManage = function assistantBoardManage(root) {
  const r = root || {}
  const vm = this.call('assistantBoardViewModel', r)
  const none = { savedId: null, canRename: false, canDelete: false, canDuplicate: false }
  if (!vm || vm.preview || vm.status !== 'ready') return none
  const savedId = this.call('assistantBoardSavedId', r, [vm.savedId, vm.boardId])
  if (!savedId) return { ...none, canRename: true }
  const row = (Array.isArray(r._aiBoards) ? r._aiBoards : []).find((b) => b && b.id === savedId)
  return {
    savedId,
    canRename: !(row && row.viewerCanEdit === false),
    canDelete: !(row && row.viewerCanDelete === false),
    canDuplicate: true
  }
}

// RENAME, step 1: the head's title becomes a field holding the title.
export const assistantBoardRenameStart = function assistantBoardRenameStart() {
  const root = this.state && this.state.root
  const b = root && root._aiBoard
  if (!b || typeof root.update !== 'function') return false
  if (!this.call('assistantBoardManage', root).canRename) return false
  const title = (b.spec && b.spec.title) || b.prompt || ''
  root.update(
    { _aiBoard: { ...b, rename: { draft: String(title).slice(0, 60) }, notice: null } },
    { preventFetch: true }
  )
  return true
}

// The field's words as they are typed (60 characters, the server's cap).
export const assistantBoardRenameDraft = function assistantBoardRenameDraft(value) {
  const root = this.state && this.state.root
  const b = root && root._aiBoard
  if (!b || !b.rename || typeof root.update !== 'function') return false
  root.update(
    { _aiBoard: { ...b, rename: { draft: String(value == null ? '' : value).slice(0, 60) } } },
    { preventFetch: true }
  )
  return true
}

export const assistantBoardRenameCancel = function assistantBoardRenameCancel() {
  const root = this.state && this.state.root
  const b = root && root._aiBoard
  if (!b || !b.rename || typeof root.update !== 'function') return false
  root.update({ _aiBoard: { ...b, rename: null } }, { preventFetch: true })
  return true
}

// RENAME, step 2: the new title on screen, in the raw spec (Save keeps it)
// and in the thread chips at once; for a saved board also on its record
// (ai.boards update { title }) and in the saved row. A refused write puts
// the old title back and says so. The board keeps its id (no re-derive).
export const assistantBoardRename = async function assistantBoardRename() {
  const root = this.state && this.state.root
  const b = root && root._aiBoard
  if (!b || !b.rename || typeof root.update !== 'function') return { ok: false }
  const title = String(b.rename.draft || '')
    .trim()
    .slice(0, 60)
  if (!title) {
    this.call('assistantBoardRenameCancel')
    return { ok: false, reason: 'empty' }
  }
  const before = (b.spec && b.spec.title) || ''
  const manage = this.call('assistantBoardManage', root)
  const put = (t, notice) => {
    const cur = root._aiBoard
    if (!cur || cur.boardId !== b.boardId) return
    const history = Array.isArray(root._aiBoardHistory) ? root._aiBoardHistory : null
    const saved = Array.isArray(root._aiBoards) ? root._aiBoards : null
    root.update(
      {
        _aiBoard: {
          ...cur,
          rename: null,
          notice: notice || null,
          spec: { ...(cur.spec || {}), title: t },
          ...(cur.raw ? { raw: { ...cur.raw, title: t } } : {})
        },
        ...(history
          ? {
              _aiBoardHistory: history.map((h) =>
                h && h.id === b.boardId ? { ...h, title: t } : h
              )
            }
          : {}),
        ...(saved && manage.savedId
          ? { _aiBoards: saved.map((x) => (x && x.id === manage.savedId ? { ...x, title: t } : x)) }
          : {})
      },
      { preventFetch: true }
    )
  }
  put(title)
  if (!manage.savedId) return { ok: true, title }
  const ws = root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  try {
    const sdk = this.sdk || this.call('getSDK')
    if (!sdk || typeof sdk.execute !== 'function') throw new Error('unknown method: ai.boards')
    await sdk.execute('ai.boards', 'update', {
      id: manage.savedId,
      patch: { title },
      ...(workspaceId ? { workspaceId } : {})
    })
    return { ok: true, title }
  } catch (err) {
    put(before, 'renameFailed')
    return { ok: false, reason: 'failed', error: (err && err.message) || String(err) }
  }
}

// DUPLICATE: ONE new record — the raw spec under "<title> (copy)" — joins the
// saved row and becomes the board on screen. Only a saved board duplicates
// (an unsaved one is saved with Save).
export const assistantBoardDuplicate = async function assistantBoardDuplicate() {
  const root = this.state && this.state.root
  const b = root && root._aiBoard
  if (!b || !b.raw || typeof root.update !== 'function') return { ok: false }
  if (!this.call('assistantBoardManage', root).canDuplicate) return { ok: false }
  const title = String(this.call('polyglot', 'workspace.assistantBoard.copyTitle'))
    .replace('{title}', (b.spec && b.spec.title) || b.prompt || '')
    .slice(0, 60)
  const ws = root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  try {
    const sdk = this.sdk || this.call('getSDK')
    if (!sdk || typeof sdk.execute !== 'function') throw new Error('unknown method: ai.boards')
    const saved = await sdk.execute('ai.boards', 'save', {
      spec: { ...b.raw, title },
      prompt: b.prompt,
      title,
      ...(workspaceId ? { workspaceId } : {})
    })
    const row = saved && typeof saved === 'object' ? saved.data || saved : null
    if (!row || !row.id) throw new Error('no record')
    const cur = root._aiBoard || b
    const list = Array.isArray(root._aiBoards) ? root._aiBoards : []
    root.update(
      {
        _aiBoards: [row, ...list.filter((x) => x && x.id !== row.id)],
        _aiBoard: {
          ...cur,
          savedId: row.id,
          save: 'saved',
          notice: null,
          spec: { ...(cur.spec || {}), title },
          raw: { ...(cur.raw || {}), title }
        }
      },
      { preventFetch: true }
    )
    return { ok: true, board: row }
  } catch (err) {
    const cur = root._aiBoard
    if (cur) {
      root.update({ _aiBoard: { ...cur, notice: 'duplicateFailed' } }, { preventFetch: true })
    }
    return { ok: false, reason: 'failed', error: (err && err.message) || String(err) }
  }
}

// DELETE, step 1: the house confirm (CRUD standard D1), only for a saved
// board the viewer may delete. Its title names the board on screen (the title
// Rename and Duplicate read); its message says what goes and that it cannot
// come back. No cancelText: the shell's Cancel would only translate the
// dialog's own default (D1 amendment, 2026-10-31). Nothing changes before its
// Delete. The confirm keeps the id of the board it asks about, so a board that
// takes its place on screen meanwhile is never the one that goes.
export const assistantBoardDeleteAsk = function assistantBoardDeleteAsk() {
  const root = this.state && this.state.root
  if (!root || !root._aiBoard) return false
  const manage = this.call('assistantBoardManage', root)
  if (!manage.canDelete || !manage.savedId) return false
  const id = manage.savedId
  const b = root._aiBoard
  const name = (b.spec && b.spec.title) || b.prompt || ''
  const t = (key) => this.call('polyglot', key)
  this.call('confirmDialog', {
    // a function replacement: a `$` in the board's title stays as typed
    title: String(t('workspace.assistantBoard.deleteTitle')).replace('{title}', () => name),
    message: t('workspace.assistantBoard.deleteMessage'),
    confirmText: t('common.action.delete'),
    confirmTheme: 'primary',
    icon: null,
    onConfirm: () => this.call('assistantBoardDelete', { id })
  })
  return true
}

// DELETE, step 2 (the confirm's Delete): the record goes (ai.boards remove),
// the saved row and the chips forget it, and the view steps aside to Home.
// Only the board the confirm named (`opts.id`), while it is still the saved
// board on screen and the viewer may delete it. A refused delete keeps
// everything and says so in the status line.
export const assistantBoardDelete = async function assistantBoardDelete(opts) {
  const root = this.state && this.state.root
  const b = root && root._aiBoard
  if (!b || typeof root.update !== 'function') return { ok: false }
  const manage = this.call('assistantBoardManage', root)
  if (!manage.canDelete || !manage.savedId) return { ok: false }
  const id = manage.savedId
  if (!opts || opts.id !== id) return { ok: false }
  const ws = root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  try {
    const sdk = this.sdk || this.call('getSDK')
    if (!sdk || typeof sdk.execute !== 'function') throw new Error('unknown method: ai.boards')
    await sdk.execute('ai.boards', 'remove', { id, ...(workspaceId ? { workspaceId } : {}) })
  } catch (err) {
    const cur = root._aiBoard
    if (cur) {
      root.update({ _aiBoard: { ...cur, notice: 'deleteFailed' } }, { preventFetch: true })
    }
    return { ok: false, reason: 'failed', error: (err && err.message) || String(err) }
  }
  const list = Array.isArray(root._aiBoards) ? root._aiBoards : []
  const history = Array.isArray(root._aiBoardHistory) ? root._aiBoardHistory : null
  const cur = root._aiBoard || b
  root.update(
    {
      _aiBoards: list.filter((x) => x && x.id !== id),
      ...(history
        ? {
            _aiBoardHistory: history.map((h) =>
              h && h.savedId === id ? { ...h, savedId: null } : h
            )
          }
        : {}),
      _aiBoard: { ...cur, savedId: null, save: null, notice: null }
    },
    { preventFetch: true }
  )
  this.call('assistantBoardDismiss', {})
  return { ok: true, id }
}

// Esc / Dismiss / the "Home" segment: the view crossfades out, the saved board
// is back, the board stays in the thread chips. Never changes the mode.
export const assistantBoardDismiss = function assistantBoardDismiss(opts) {
  const root = this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  const b = root._aiBoard
  // An explicit open ends with the view (the address drops ?board on the next
  // mirror — functions/homeBoardUrl.js reads the current segment).
  if (root._aiBoardExplicit) root.update({ _aiBoardExplicit: null }, { preventFetch: true })
  if (b && !b.dismissed) {
    root.update({ _aiBoard: { ...b, dismissed: true } }, { preventFetch: true })
  }
  if (root._aiBoardPreview === true) {
    root.update({ _aiBoardPreview: false }, { preventFetch: true })
  }
  if (opts && opts.focusComposer) this.call('assistantBoardFocus', 'composer')
  return true
}

// A thread chip: that board again, re-derived from its stored spec against
// today's installed apps (an app removed since is counted, not shown).
export const assistantBoardShow = function assistantBoardShow(id) {
  const root = this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  const list = Array.isArray(root._aiBoardHistory) ? root._aiBoardHistory : []
  const entry = list.find((h) => h && h.id === id)
  if (!entry || !entry.raw) return false
  const derived = this.call('boardItemsFor', entry.raw, root)
  const ws = root.workspace
  const savedId = this.call('assistantBoardSavedId', root, [
    entry.id,
    entry.savedId,
    derived.boardId
  ])
  // A chip re-shows the board where it is clicked from (AI_ASSISTANT_SPEC
  // §12.2 — the same "belongs to the route" rule a fresh ask tags with at
  // assistantBoardGenerate's `madeOn`), not wherever it was first made.
  const shownOn = typeof root.currentPath === 'string' && root.currentPath ? root.currentPath : '/'
  root.update(
    {
      _aiBoard: {
        v: 1,
        status: 'ready',
        requestId: 'history_' + entry.id,
        workspaceId: (ws && (ws.id || ws._id)) || null,
        path: shownOn,
        prompt: entry.prompt,
        op: 'generate',
        refineOf: null,
        threadId: null,
        startedAt: entry.at || null,
        expected: derived.items.length,
        sizes: null,
        raw: entry.raw,
        boardId: derived.boardId,
        spec: derived.spec,
        items: derived.items,
        total: derived.total,
        dropped: derived.dropped,
        error: null,
        prev: null,
        pins: {},
        pinAll: null,
        save: savedId ? 'saved' : null,
        savedId,
        notice: null,
        dismissed: false,
        preview: false,
        receivedAt: entry.at || null
      }
    },
    { preventFetch: true }
  )
  return true
}

// THE BOARD FOLLOWS THE INSTALLED VOCABULARY (qa round 11 F1). A board's rows
// are derived from its raw spec against the widgets the INSTALLED apps publish
// (boardVocabularyFor ← `workspaceApps` / `workspaceProject`). A cold
// `?board=` load shows the board at boot, before the module apps publish
// (functions/workspaceProjects.js), so a widget item read "not installed" and
// the board stayed "3 of 4" for good — the CRM funnel never rendered, while
// the same board opened from its tab after boot showed 4 of 4. The view
// subscribes to those root keys (components/AssistantBoardView.js) and calls
// this on every change: a READY board of this workspace with its raw spec
// re-derives, and writes ONLY when its rows changed (pins, save and saved id
// kept). Loading / streaming boards re-derive per chunk already
// (applyBoardChunk); a preview derives on read.
export const assistantBoardRederive = function assistantBoardRederive(root) {
  const r = root || (this && this.state && this.state.root) || null
  const vm = r && r._aiBoard
  if (!vm || typeof vm !== 'object' || typeof r.update !== 'function') return false
  if (vm.status !== 'ready' || vm.preview || !vm.raw) return false
  const ws = r.workspace
  const live = (ws && (ws.id || ws._id)) || null
  if (vm.workspaceId && live && String(vm.workspaceId) !== String(live)) return false
  const derived = this.call('boardItemsFor', vm.raw, r)
  // A row's link counts too: a site card's ↗ names its website out of the
  // project list and an App Store app out of the App Store list, both of
  // which can land after the board (MISSING-NAMES-2).
  const rows = (items) =>
    (Array.isArray(items) ? items : [])
      .map((it) =>
        [
          it.kind,
          it.key,
          it.id,
          typeof it.body === 'string' ? it.body : '',
          typeof it.openRoute === 'string' ? it.openRoute : ''
        ].join(':')
      )
      .join('|')
  if (rows(derived.items) === rows(vm.items) && derived.total === vm.total) return false
  r.update(
    {
      _aiBoard: {
        ...vm,
        spec: derived.spec,
        items: derived.items,
        total: derived.total,
        dropped: derived.dropped,
        expected: derived.items.length
      }
    },
    { preventFetch: true }
  )
  return true
}

// The thread chips' rows: the recent boards of THIS workspace, newest first.
export const assistantBoardChips = function assistantBoardChips(root) {
  const r = root || {}
  const list = Array.isArray(r._aiBoardHistory) ? r._aiBoardHistory : []
  const ws = r.workspace
  const live = (ws && (ws.id || ws._id)) || null
  return list
    .filter((h) => h && h.id && (!h.workspaceId || !live || String(h.workspaceId) === String(live)))
    .slice(0, 5)
    .map((h) => ({ value: h.id, label: h.title || h.prompt, prompt: h.prompt }))
}

// ── Pin (the existing AI-widget create path) ─────────────────────────────────
// The record a pin writes — one `workspaceAiWidgets` row through
// functions/aiWorkspaceOps.js `aiCreateWidget`:
//   widget item → { title, icon, hue, spec, widget: '<app>.<id>', params }
//   block item  → { title, icon, hue, spec, block: { type, data, format?, source } }
// The `widget` / `block` fields are the CONTRACT this view proposes to core
// (spec §3 "carrying { widget, params } or the block"): today's widgetDefs
// schema and aiCreateWidget's own guard accept only `{ dataRecipe, body }`, so
// a pin answers "could not pin" until CORE-AI-BOARDS-GENERATE-VOCABULARY-1
// lands both halves (docs/patches/assistant-board-pin-def.patch).
export const boardItemWidgetDef = function boardItemWidgetDef(item) {
  if (!item || typeof item !== 'object') return null
  const size = item.size === 's' || item.size === 'l' || item.size === 'xl' ? item.size : 'm'
  const spec =
    size === 'xl'
      ? { w: 12, h: 3 }
      : size === 'l'
        ? { w: 6, h: 6 }
        : size === 's'
          ? { w: 3, h: 2 }
          : { w: 6, h: 3 }
  const title = String(item.title || item.id || '').slice(0, 60)
  const base = {
    title,
    icon: item.icon || 'star',
    ...(item.hue ? { hue: item.hue } : {}),
    spec: { ...spec, minW: 2, minH: 2 }
  }
  if (item.kind === 'widget') return { ...base, widget: item.ref, params: item.params || {} }
  // A RECORDS card pins its block by reference (WIDGET-TOOLS-RECORDS-SHAPE-1):
  // the server validates it against the member's collections and the home
  // reads its rows live for each viewer. A chart hint rides only on a chart.
  if (item.kind === 'records') {
    const r = item.records && typeof item.records === 'object' ? item.records : null
    if (!r) return null
    const records = { ...r, kind: 'records' }
    if (records.view !== 'chart') delete records.chart
    return { ...base, records }
  }
  const def = item.body && Array.isArray(item.body.blocks) ? item.body.blocks[0] : null
  return {
    ...base,
    block: {
      type: item.block,
      data: item.data || {},
      ...(def && def.format ? { format: def.format } : {}),
      source: item.source || {}
    }
  }
}

// The duplicate key for a board item OR a stored widget row: the same widget
// with the same params, the same block with the same data, or — for an AI
// widget the assistant created in chat — the same dataRecipe + body.
//
// The SHAPE names the fields, never `kind` alone. A board item names its
// widget `ref` and its block `block` (the type) + `data`; a stored row names
// them `widget` and `block: { type, data }` — and since the widget-defs server
// accepts board items (CORE-WIDGETDEFS-ACCEPT-BOARD-ITEMS-1) a stored row
// carries `kind` too. Read through the item branch, a row hashed
// `widget: undefined`, so a second pin of the same widget + params was never
// seen as a duplicate: it went to the server and said "could not be pinned"
// (live, dev Unreal twin, 2026-09-29).
export const boardItemPinHash = function boardItemPinHash(itemOrRow) {
  const x = itemOrRow && typeof itemOrRow === 'object' ? itemOrRow : {}
  // A records card / row: the QUERY is its identity — the server's pin key
  // fields (widgets recordsPinKey: never the title or the chart hint), the
  // period without the answer clock a board card carries. Without this branch
  // every records row hashed as "no recipe, no body" and the second records
  // pin read as a duplicate of the first.
  const rec = x.records && typeof x.records === 'object' ? x.records : null
  if (x.kind === 'records' || rec) {
    const r = rec || {}
    const p = r.period && typeof r.period === 'object' ? r.period : null
    return this.call('boardSpecHash', {
      records: {
        app: r.app || '',
        collection: r.collection || '',
        view: r.view || '',
        filters: Array.isArray(r.filters) ? r.filters : [],
        period: p ? { field: p.field, preset: p.preset } : null,
        aggregate: r.aggregate || null,
        groupBy: r.groupBy || null,
        sort: r.sort || null,
        limit: r.limit != null ? r.limit : null,
        fields: Array.isArray(r.fields) ? r.fields : []
      }
    })
  }
  const widget =
    typeof x.ref === 'string' && x.ref ? x.ref : typeof x.widget === 'string' ? x.widget : ''
  if (x.kind === 'widget' || widget) {
    return this.call('boardSpecHash', { widget, params: x.params || {} })
  }
  const blk = x.block
  const blockRow = !!blk && typeof blk === 'object'
  if (x.kind === 'block' || blockRow) {
    return this.call('boardSpecHash', {
      block: {
        type: blockRow ? blk.type : blk,
        data: (blockRow ? blk.data : x.data) || {}
      }
    })
  }
  return this.call('boardSpecHash', { dataRecipe: x.dataRecipe || null, body: x.body || null })
}

// Pin ONE card: refuse a duplicate (a stored row with the same hash, or a pin
// already made or in flight from this board), write through aiCreateWidget,
// record the outcome on the board. A preview board never pins.
export const pinBoardItem = async function pinBoardItem(el, s, item) {
  const host = el || this
  const root = (s && s.root) || (host && host.state && host.state.root)
  const board = root && root._aiBoard
  if (!root || typeof root.update !== 'function' || !item || !item.key) {
    return { ok: false, reason: 'unavailable' }
  }
  // A records card pins its block (WIDGET-TOOLS-RECORDS-SHAPE-1): the widget
  // defs store kind 'records' and the home draws it with AssistantRecordsBlock.
  if (!board || board.preview || board.status !== 'ready' || item.kind === 'skeleton') {
    return { ok: false, reason: 'unavailable' }
  }
  const mark = (state, notice) => {
    const b = root._aiBoard
    if (!b || b.requestId !== board.requestId) return
    root.update(
      {
        _aiBoard: {
          ...b,
          pins: { ...(b.pins || {}), [item.key]: state },
          ...(notice !== undefined ? { notice } : {})
        }
      },
      { preventFetch: true }
    )
  }
  const current = (board.pins || {})[item.key]
  if (current === 'pinned' || current === 'pinning') return { ok: false, reason: 'duplicate' }
  const hash = host.call('boardItemPinHash', item)
  const rows = Array.isArray(root.workspaceAiWidgets) ? root.workspaceAiWidgets : []
  if (rows.some((row) => host.call('boardItemPinHash', row) === hash)) {
    mark('duplicate', 'pinDuplicate')
    return { ok: false, reason: 'duplicate' }
  }
  mark('pinning', null)
  let res = null
  try {
    res = await host.call('aiCreateWidget', host.call('boardItemWidgetDef', item))
  } catch (err) {
    res = { ok: false, error: (err && err.message) || String(err) }
  }
  if (res && res.ok) {
    mark('pinned', null)
    return { ok: true }
  }
  mark('failed', 'pinFailed')
  return { ok: false, reason: 'failed', error: res && res.error }
}

// Pin every card not pinned yet, one after another (each write is the
// single-flight create; a batch would race the widget list refresh).
export const pinAllBoardItems = async function pinAllBoardItems(el, s) {
  const host = el || this
  const root = (s && s.root) || (host && host.state && host.state.root)
  const board = root && root._aiBoard
  if (!root || !board || board.preview || board.status !== 'ready') return { ok: false, pinned: 0 }
  const setAll = (state) => {
    const b = root._aiBoard
    if (b && b.requestId === board.requestId) {
      root.update({ _aiBoard: { ...b, pinAll: state } }, { preventFetch: true })
    }
  }
  setAll('pinning')
  let pinned = 0
  let failed = 0
  for (const item of board.items || []) {
    const state = ((root._aiBoard && root._aiBoard.pins) || {})[item.key]
    if (state === 'pinned' || state === 'duplicate' || item.kind === 'records') continue
    const res = await host.call('pinBoardItem', host, { root }, item)
    if (res && res.ok) pinned++
    else if (!res || res.reason !== 'duplicate') failed++
  }
  setAll(failed ? null : 'pinned')
  return { ok: !failed, pinned, failed }
}

// ── Is Page mode offered? (`_aiBoardsAvailable`) ─────────────────────────────
// ws-w2 offers Page mode — the "Answers" choice, the composer's Page wording,
// the send to a board — only while the board service answers for THIS
// workspace (functions/assistantDock.js `assistantPageAvailable`). This is the
// answer: `ai.boards.vocabulary` (the installed-apps catalogue the model is
// given, spec §3) either answers or it does not. Today the SDK has no
// `ai.boards` entity → false, and Page mode stays unoffered — nothing in the
// product changes until core's service exists. A reply for a workspace the
// user has already left is dropped (the key is workspace-scoped).
export const assistantBoardProbe = async function assistantBoardProbe(root, sdk) {
  if (!root || typeof root.update !== 'function') return false
  const ws = root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  let ok = false
  try {
    if (!sdk || typeof sdk.execute !== 'function') throw new Error('unknown method: ai.boards')
    const res = await sdk.execute('ai.boards', 'vocabulary', workspaceId ? { workspaceId } : {})
    ok = res != null && typeof res === 'object'
  } catch (_) {
    ok = false
  }
  const now = root.workspace
  const still = (now && (now.id || now._id)) || null
  if (still !== workspaceId) return false
  if (root._aiBoardsAvailable !== ok)
    root.update({ _aiBoardsAvailable: ok }, { preventFetch: true })
  return ok
}

// THE GRID MEASURE IS A LAYOUT READ, and `onUpdate` runs INSIDE the render
// pass, so `getBoundingClientRect` there flushes style + layout for the whole
// page in the middle of that pass's own writes. On the home the same pass has
// just stamped `data-under-view` / `inert` / `aria-hidden` on all 21 cards
// (functions/homeWidgetFrame.js homeWidgetUnderView), so the read pays for
// every one of them, and it fires TWICE per submit because its own
// `s.update({ cols, gutter, cell })` re-enters `onUpdate`. MEASURED on dev
// 2026-10-02 (/w/vault/default, Chrome MCP, a tab that never paints): 15.5 ms
// per read on a quiet box and 113.9-169.5 ms on a loaded one, inside the ONE
// task the first skeleton waits on
// (DESIGN-WL-ASSISTANT-BOARD-SUBMIT-BLOCKS-THE-MAIN-THREAD-FOR-A-SECOND-1).
// So the FIRST read stays synchronous — the first render needs its columns —
// and every later one lands in its OWN task, never inside a pass. A resize
// reads straight away (`onWindowResize` is not a render pass), and the read is
// a no-op write whenever the width did not change, which is why moving it off
// the pass changes no geometry. `setTimeout`, not rAF or a ResizeObserver:
// neither of those fires in a hidden tab, where this view is still measured.
// `sync` names the reader (the Fly view's own: assistantFlySyncGrid).
export const assistantBoardGridRead = function assistantBoardGridRead(el, s, sync) {
  const host = el || this
  if (!host) return false
  const reader = typeof sync === 'string' && sync ? sync : 'assistantBoardSyncGrid'
  if (!host.__boardGridRead) {
    host.__boardGridRead = true
    return this.call(reader, host, s)
  }
  if (host.__boardGridQueued) return false
  const again = this
  const read = () => {
    host.__boardGridQueued = null
    if (host.node && host.node.isConnected === false) return
    again.call(reader, host, s)
  }
  host.__boardGridQueued = setTimeout(read, 0)
  return false
}

// A view taken off the page drops its pending grid read with it.
export const assistantBoardGridReadOff = function assistantBoardGridReadOff(el) {
  const host = el || this
  if (!host || !host.__boardGridQueued) return false
  clearTimeout(host.__boardGridQueued)
  host.__boardGridQueued = null
  return true
}

// The view's mount (onMounted) and every update after it (`subscribeTo:
// ['workspace']` re-runs it on a switch): read the grid; then, ONCE per
// workspace, ask whether Page mode exists here and list the saved boards.
export const assistantBoardMounted = function assistantBoardMounted(el, s) {
  const host = el || this
  this.call('assistantBoardGridRead', host, s)
  const root = s && s.root
  const ws = root && root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  if (host.__boardProbed && host.__boardProbedFor === workspaceId) return false
  host.__boardProbed = true
  host.__boardProbedFor = workspaceId
  const sdk = host.sdk || this.call('getSDK')
  this.call('assistantBoardProbe', root, sdk)
  this.call('listBoards', root, sdk)
  return true
}

// ── Saved boards (`ai.boards` — the server half is core's) ───────────────────
// Save the board on screen as a named board (one `ai_boards` record).
export const saveBoard = async function saveBoard(el, s) {
  const host = el || this
  const root = (s && s.root) || (host && host.state && host.state.root)
  const board = root && root._aiBoard
  if (!root || !board || board.preview || board.status !== 'ready' || !board.raw) {
    return { ok: false, reason: 'unavailable' }
  }
  // Already a saved record: nothing to write twice.
  if (board.save === 'saved' && board.savedId) return { ok: true, already: true }
  const mark = (save, notice, savedId) => {
    const b = root._aiBoard
    if (b && b.requestId === board.requestId) {
      root.update(
        { _aiBoard: { ...b, save, notice, ...(savedId ? { savedId } : {}) } },
        { preventFetch: true }
      )
    }
  }
  const ws = root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  mark('saving', null)
  try {
    const sdk = host.sdk || host.call('getSDK')
    if (!sdk || typeof sdk.execute !== 'function') throw new Error('unknown method: ai.boards')
    const saved = await sdk.execute('ai.boards', 'save', {
      spec: board.raw,
      prompt: board.prompt,
      title: (board.spec && board.spec.title) || board.prompt,
      ...(workspaceId ? { workspaceId } : {})
    })
    const row = saved && typeof saved === 'object' ? saved.data || saved : null
    if (row && row.id) {
      const list = Array.isArray(root._aiBoards) ? root._aiBoards : []
      // The session's copy of this board learns the record it became, so a
      // chip that re-opens it reads "Saved" too.
      const history = Array.isArray(root._aiBoardHistory) ? root._aiBoardHistory : null
      root.update(
        {
          _aiBoards: [row, ...list.filter((x) => x && x.id !== row.id)],
          ...(history
            ? {
                _aiBoardHistory: history.map((h) =>
                  h && h.id === board.boardId ? { ...h, savedId: row.id } : h
                )
              }
            : {})
        },
        { preventFetch: true }
      )
    }
    mark('saved', null, row && row.id)
    return { ok: true, board: row }
  } catch (err) {
    mark('failed', 'saveFailed')
    return { ok: false, reason: 'failed', error: (err && err.message) || String(err) }
  }
}

// SHARE (AI_ASSISTANT_SPEC v1 §8; ASSISTANT_BOARD_MODE_SPEC §4 "Share = the
// record's link (workspace members)"): the board is saved first when it is
// not yet a record, made visible to the workspace, and its link — the home
// with `?board=<id>` (functions/homeBoardUrl.js opens it) — is copied, with
// ONE notification in the board's own words (never the public-link copier's
// "Anyone with this link": a board link opens for workspace members only).
// The clipboard write is fire-and-forget — it can hang on a permission prompt
// (shareLinks.js' measured note); the link is the result either way. The
// state rides the board: share 'sharing' | 'shared' | 'failed'.
export const assistantBoardShare = async function assistantBoardShare(el, s) {
  const host = el || this
  const root = (s && s.root) || (host && host.state && host.state.root)
  const board = root && root._aiBoard
  if (!root || !board || board.preview || board.status !== 'ready' || !board.raw) {
    return { ok: false, reason: 'unavailable' }
  }
  const mark = (share) => {
    const b = root._aiBoard
    if (b && b.requestId === board.requestId) {
      root.update({ _aiBoard: { ...b, share } }, { preventFetch: true })
    }
  }
  if (board.share === 'sharing') return { ok: false, reason: 'busy' }
  mark('sharing')
  try {
    let id = board.save === 'saved' && board.savedId ? board.savedId : null
    if (!id) {
      const saved = await host.call('saveBoard', host, { root })
      id =
        saved && saved.board && saved.board.id
          ? saved.board.id
          : root._aiBoard && root._aiBoard.savedId
    }
    if (!id) throw new Error('not saved')
    const sdk = host.sdk || host.call('getSDK')
    const ws = root.workspace
    const workspaceId = (ws && (ws.id || ws._id)) || null
    if (sdk && typeof sdk.execute === 'function') {
      await sdk.execute('ai.boards', 'update', {
        id,
        patch: { visibility: 'workspace' },
        ...(workspaceId ? { workspaceId } : {})
      })
    }
    const loc = typeof window !== 'undefined' && window.location ? window.location : null
    const base = loc ? String(loc.origin || '') + String(loc.pathname || '/') : '/'
    const url = base + '?board=' + encodeURIComponent(id)
    mark('shared')
    const say = (ok) => {
      try {
        host.call('openNotification', {
          type: ok ? 'success' : 'error',
          title: host.call(
            'polyglot',
            ok ? 'workspace.assistantBoard.linkCopied' : 'workspace.share.copyFailed.title'
          ),
          message: ok ? host.call('polyglot', 'workspace.assistantBoard.linkCopiedHint') : url
        })
      } catch (_) {
        /* no notification host (a test, a standalone surface) */
      }
    }
    const clip =
      typeof navigator !== 'undefined' && navigator.clipboard ? navigator.clipboard : null
    if (clip && typeof clip.writeText === 'function') {
      Promise.resolve(clip.writeText(url)).then(
        () => say(true),
        () => say(false)
      )
    } else say(false)
    return { ok: true, url }
  } catch (err) {
    mark('failed')
    return { ok: false, reason: 'failed', error: (err && err.message) || String(err) }
  }
}

// The Share button's label index (AppLabelStack: share · sharing · link copied).
export const assistantBoardShareLabel = function assistantBoardShareLabel(vm) {
  const share = vm && vm.share
  return share === 'sharing' ? 1 : share === 'shared' ? 2 : 0
}

// OPEN IN SIDEBAR (AI_ASSISTANT_SPEC v1 §1 "Open in sidebar / Open in main move
// the SAME thread"): the board leaves the grid area and lands in the docked
// panel's thread as a 'board' block (components/AssistantThreadBlock.js —
// its Open shows the board over the home again). The row is LOCAL to this
// session (`local: true`, never sent to the model or the server), like an
// answer view; the drawer opens on its last side.
export const assistantBoardToSidebar = function assistantBoardToSidebar() {
  const root = this && this.state && this.state.root
  const board = root && root._aiBoard
  if (!root || typeof root.update !== 'function' || !board || board.status !== 'ready') return false
  const boardId = board.boardId || (board.spec && board.spec.id) || null
  if (!boardId) return false
  const card = {
    type: 'board',
    boardId,
    title: (board.spec && board.spec.title) || board.prompt || '',
    summary: (board.spec && board.spec.summary) || '',
    count: Array.isArray(board.items) ? board.items.length : 0
  }
  const list = Array.isArray(root.chatMessages) ? root.chatMessages : []
  const row = {
    id: 'board-' + boardId + '-' + Date.now().toString(36),
    role: 'assistant',
    text: '',
    ts: Date.now(),
    local: true,
    cards: [card]
  }
  this.call('assistantBoardDismiss')
  root.update({ chatMessages: list.concat(row), _homeThreadOpen: false, assistantOpen: true })
  return true
}

// The saved boards of the workspace on screen → `root._aiBoards` (the boards
// row reads it). No server yet → an empty list, and the row stays hidden.
export const listBoards = async function listBoards(root, sdk) {
  if (!root || typeof root.update !== 'function') return []
  const ws = root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  let rows = []
  try {
    if (!sdk || typeof sdk.execute !== 'function') throw new Error('unknown method: ai.boards')
    const res = await sdk.execute('ai.boards', 'list', workspaceId ? { workspaceId } : {})
    const list = Array.isArray(res) ? res : (res && (res.data || res.items)) || []
    rows = list.filter((x) => x && x.id)
  } catch (_) {
    rows = []
  }
  const live = root.workspace
  const still = (live && (live.id || live._id)) || null
  if (still === workspaceId) root.update({ _aiBoards: rows }, { preventFetch: true })
  return rows
}

// One saved board's spec (`?board=<id>` renders the same spec).
export const getBoard = async function getBoard(root, sdk, id) {
  if (!id) return { ok: false, reason: 'missing' }
  const ws = root && root.workspace
  const workspaceId = (ws && (ws.id || ws._id)) || null
  try {
    if (!sdk || typeof sdk.execute !== 'function') throw new Error('unknown method: ai.boards')
    const res = await sdk.execute('ai.boards', 'get', {
      id,
      ...(workspaceId ? { workspaceId } : {})
    })
    const row = res && typeof res === 'object' ? res.data || res : null
    const spec = row && (row.spec || (Array.isArray(row.items) ? row : null))
    return spec ? { ok: true, spec, board: row } : { ok: false, reason: 'missing' }
  } catch (err) {
    return { ok: false, reason: 'unavailable', error: (err && err.message) || String(err) }
  }
}

// ── The boards row hook (ws-home mounts the row; spec §4 decision 24) ────────
// Rows for an AppSegmented: Home · the board on screen (when it is not a saved
// one) · the saved boards. The row shows only when there is more than Home.
export const assistantBoardSegments = function assistantBoardSegments(root) {
  const r = root || {}
  const vm = this.call('assistantBoardViewModel', r)
  const saved = Array.isArray(r._aiBoards) ? r._aiBoards : []
  const rows = [{ key: 'home', text: 'workspace.assistantBoard.homeBoard', value: 'home' }]
  const onScreen = vm && !vm.preview && vm.boardId && vm.status !== 'failed' ? vm.boardId : null
  // A saved board on screen is ITS saved segment — never listed twice.
  const savedAs = onScreen ? this.call('assistantBoardSavedId', r, [vm.savedId, onScreen]) : null
  if (onScreen && !savedAs) {
    rows.push({ key: onScreen, text: (vm.spec && vm.spec.title) || vm.prompt, value: onScreen })
  }
  // The LIVE DECK made on the home (Presentation, functions/assistantDeck.js)
  // takes a segment like a board; a saved one is its saved row below.
  const fns = this.context && this.context.functions
  const deck =
    fns && typeof fns.assistantDeckViewModel === 'function'
      ? this.call('assistantDeckViewModel', r)
      : null
  const deckPath = deck && typeof deck.path === 'string' && deck.path ? deck.path : '/'
  const deckRoute = this.call('stripWorkspacePrefix', deckPath)
  const deckHome = deckRoute === '/' || deckRoute === ''
  if (deck && deckHome && !deck.preview && deck.id && deck.status === 'ready' && !deck.savedId) {
    rows.push({ key: deck.id, text: deck.title || deck.prompt, value: deck.id })
  }
  for (const b of saved) {
    if (b && b.id) rows.push({ key: b.id, text: b.title || b.id, value: b.id })
  }
  return rows.length > 1 ? rows : []
}

// The boards row's accessible name ("Boards").
export const assistantBoardRowLabel = function assistantBoardRowLabel() {
  return this.call('polyglot', 'workspace.assistantBoard.boards')
}

// The segment the row shows as current: the board on screen, else Home.
export const assistantBoardCurrentSegment = function assistantBoardCurrentSegment(root) {
  const r = root || {}
  const vm = this.call('assistantBoardViewModel', r)
  // A LIVE DECK on screen is its own segment (its saved row when saved).
  const fns = this.context && this.context.functions
  if (fns && typeof fns.assistantDeckShown === 'function' && this.call('assistantDeckShown', r)) {
    const deck = this.call('assistantDeckViewModel', r)
    if (deck && (deck.savedId || deck.id)) return deck.savedId || deck.id
  }
  if (!this.call('assistantBoardShown', r) || !vm || !vm.boardId) return 'home'
  return this.call('assistantBoardSavedId', r, [vm.savedId, vm.boardId]) || vm.boardId
}

// AppSegmented's `select` (a function NAME, called as el.call(select, value)):
// Home dismisses the view; a board id shows that board — from the thread
// chips' spec when this session made it, else from its saved record.
export const assistantBoardSelect = async function assistantBoardSelect(value) {
  const root = this.state && this.state.root
  if (!root) return false
  const fns = this.context && this.context.functions
  const hasDeck = !!(fns && typeof fns.assistantDeckOpenSaved === 'function')
  if (value === 'home') {
    if (hasDeck) this.call('assistantDeckDismiss')
    return this.call('assistantBoardDismiss', {})
  }
  // A LIVE DECK (Presentation, functions/assistantDeck.js): the deck on screen
  // is already there (a dismissed one comes back); a SAVED deck opens as a
  // deck — its record's spec is a DeckSpec (kind 'deck', deck contract §6).
  const deck = root._aiDeck
  if (hasDeck && deck && (deck.id === value || deck.savedId === value)) {
    if (deck.dismissed && typeof root.update === 'function') {
      root.update({ _aiDeck: { ...deck, dismissed: false } }, { preventFetch: true })
    }
    return true
  }
  const savedRow = (Array.isArray(root._aiBoards) ? root._aiBoards : []).find(
    (b) => b && b.id === value
  )
  if (hasDeck && savedRow && savedRow.kind === 'deck') {
    const sdk = this.sdk || this.call('getSDK')
    const got = await this.call('getBoard', root, sdk, value)
    if (!got || !got.ok) return false
    return this.call('assistantDeckOpenSaved', { ...(got.board || savedRow), spec: got.spec })
  }
  // An EXPLICIT open (a board tab, a cold `?board=` link): the board shows
  // whatever the placement or the mode (assistantBoardShown), and the main
  // thread steps aside so ONE view holds the grid area.
  if (fns && typeof fns.assistantMainThreadClose === 'function')
    this.call('assistantMainThreadClose')
  if (typeof root.update === 'function') {
    root.update({ _aiBoardExplicit: String(value) }, { preventFetch: true })
  }
  if (this.call('assistantBoardShow', value)) return true
  const sdk = this.sdk || this.call('getSDK')
  const res = await this.call('getBoard', root, sdk, value)
  if (!res || !res.ok) return false
  // A cold `?board=<id>` link can name a saved DECK the list has not shown yet.
  if (hasDeck && res.spec && res.spec.kind === 'deck') {
    if (typeof root.update === 'function')
      root.update({ _aiBoardExplicit: null }, { preventFetch: true })
    return this.call('assistantDeckOpenSaved', { ...(res.board || {}), id: value, spec: res.spec })
  }
  const list = Array.isArray(root._aiBoardHistory) ? root._aiBoardHistory : []
  const ws = root.workspace
  const entry = {
    id: value,
    title: (res.board && res.board.title) || res.spec.title || '',
    prompt: res.spec.prompt || '',
    raw: res.spec,
    workspaceId: (ws && (ws.id || ws._id)) || null,
    at: Date.now()
  }
  root.update(
    { _aiBoardHistory: [entry, ...list.filter((h) => h && h.id !== value)].slice(0, 5) },
    { preventFetch: true }
  )
  return this.call('assistantBoardShow', value)
}
